import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  Suspense,
} from "react";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import {
  Card,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Checkbox,
  Typography,
  Tabs,
  Tag,
  Alert,
  Spin,
} from "antd";

const { Text } = Typography;
const { TabPane } = Tabs;
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import ResizableGridContainer from "../Shared/Utils/ResizableGridContainer";
import { secureExportToExcel } from "../../../../utils/secureExcelUtils";
import {
  PlusOutlined,
  UploadOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SaveOutlined,
  FileExcelOutlined,
  SwapOutlined,
  DeleteOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";
import {
  getAllBankStatementsParallel,
  getAllPartnerOverpayments,
  getAllCash,
  getAllCredits,
  getAllPartners,
  getAllStatements,
  getTransactionById,
  getCreditById,
  getCashById,
  getOverpaymentDetails,
  updateTransaction,
  getAllBanks,
  getStatementById,
  getBankById,
  updateStatement,
  deleteCash,
  deleteCredit,
  deleteTransaction,
  getTransactionsAllocationsStatistics,
  getUnifiedTransactions,
  // Quick first-page functions for hybrid loading
  getFirstPageBankStatements,
  getFirstPageOverpayments,
  getFirstPageCash,
  getFirstPageCredits,
} from "../../api/accounting";
import { deleteOverpayment } from "../../api/overpayment";
import {
  getCachedData,
  setCachedData,
  invalidateCacheByPrefix,
  CacheTTL,
} from "../../context/AccountingDataCache";
import { getUnifiedTransactionsColumns } from "./agGridColumns";
import PaymentAllocation from "../Shared/Allocation/PaymentAllocation";
import CreditAllocation from "../Shared/Allocation/CreditAllocation";
import CashAllocation from "../Shared/Allocation/CashAllocation";
import OverPaymentAllocation from "../Shared/Allocation/OverPaymentAllocation";
import OverPaymentPage from "./OverPaymentPage";
import CreateStatement from "../Shared/Forms/CreateStatement";
import UploadBankStatementWithMapping from "../Shared/Utils/UploadBankStatementWithMapping";
import StatementHistory from "../Shared/Modals/StatementHistory";
const TransactionsHistory = React.lazy(() => import("./TransactionsHistory"));
const AllocationHistory = React.lazy(() => import("./AllocationHistory"));
const PartnerTransactions = React.lazy(() => import("./PartnerTransactions"));
import CreateCashModal from "../Shared/Forms/CreateCashModal";
import CreateCreditModal from "../Shared/Forms/CreateCreditModal";
import InvoiceViewModal from "../Shared/Modals/InvoiceViewModal";
const EMPTY_ARRAY = [];
const { RangePicker } = DatePicker;

const TransactionsAllocations = ({
  selectedBrands: selectedBrandsProp = [],
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBrands: globalSelectedBrands } = useGlobalFilters();

  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const {
    refreshKey: currencyRefreshKey,
    displayCurrency,
    exchangeRate,
    isConverted,
    decimalPrecision,
  } = useDisplayCurrency();

  // Handle tab parameter from URL
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      setActiveTabKey(tabParam);
      // Remove tab param after setting it
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("tab");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Ref to force refresh when global filters change
  const forceRefreshRef = useRef(false);

  // Listen for global brand/client changes to invalidate cache and trigger refresh
  useEffect(() => {
    const handleGlobalBrandChanged = () => {
      // Invalidate all transaction caches when brand changes
      invalidateCacheByPrefix("transactions_");
      invalidateCacheByPrefix("bank_statements_");
      invalidateCacheByPrefix("overpayments_");
      invalidateCacheByPrefix("cash_");
      invalidateCacheByPrefix("credits_");
      invalidateCacheByPrefix("statistics_");
      // Mark for force refresh
      forceRefreshRef.current = true;
      setHasLoadedForBrands(false);
      lastLoadedCacheKeyRef.current = null;
    };

    const handleGlobalClientChanged = () => {
      // When client changes, brands will also change, so invalidate everything
      invalidateCacheByPrefix("transactions_");
      invalidateCacheByPrefix("bank_statements_");
      invalidateCacheByPrefix("overpayments_");
      invalidateCacheByPrefix("cash_");
      invalidateCacheByPrefix("credits_");
      invalidateCacheByPrefix("statistics_");
      // Mark for force refresh
      forceRefreshRef.current = true;
      setHasLoadedForBrands(false);
      lastLoadedCacheKeyRef.current = null;
    };

    window.addEventListener("globalBrandChanged", handleGlobalBrandChanged);
    window.addEventListener("globalClientChanged", handleGlobalClientChanged);

    return () => {
      window.removeEventListener(
        "globalBrandChanged",
        handleGlobalBrandChanged
      );
      window.removeEventListener(
        "globalClientChanged",
        handleGlobalClientChanged
      );
    };
  }, []);

  // Determine selected brands (prioritize prop, then use global filters, NEVER fall back to profile brands)
  // CRITICAL: Only show data for explicitly selected brands to prevent showing other clients' data
  const selectedBrands = useMemo(() => {
    if (selectedBrandsProp && selectedBrandsProp.length > 0) {
      return selectedBrandsProp;
    }
    // Use global filters - this ensures only selected brands are shown
    // If no brands are selected in global filters, return empty array (don't show all profile brands)
    return globalSelectedBrands || EMPTY_ARRAY;
  }, [selectedBrandsProp, globalSelectedBrands]);

  const brandsKey = useMemo(() => {
    if (!selectedBrands || selectedBrands.length === 0) return "";
    return selectedBrands
      .map((brand) => brand.id)
      .filter((id) => id !== undefined && id !== null)
      .sort()
      .join(",");
  }, [selectedBrands]);

  // Type filters - must be defined before cacheKey
  // PERSIST type filters in localStorage so they survive page refreshes and allocations
  const [selectedTypes, setSelectedTypes] = useState(() => {
    try {
      const saved = localStorage.getItem("transactions-type-filters");
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      // Ignore parse errors
    }
    return {
      bankStatement: true,
      overpayment: true,
      cash: true,
      credit: true,
    };
  });

  // Save type filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        "transactions-type-filters",
        JSON.stringify(selectedTypes)
      );
    } catch (e) {
      // Ignore storage errors
    }
  }, [selectedTypes]);

  // Date filter for statistics - must be defined before cacheKey
  const [dateFilter, setDateFilter] = useState(null);

  // Cache key for persistent storage - does NOT include selectedTypes to enable client-side filtering
  // selectedTypes filtering is done client-side to avoid refetching when toggling checkboxes
  const cacheKey = useMemo(() => {
    if (!brandsKey) return null;
    const dateKey =
      dateFilter?.[0]?.format("YYYY-MM-DD") +
        "_" +
        dateFilter?.[1]?.format("YYYY-MM-DD") || "none";
    return `transactions-allocations-cache-${brandsKey}-${dateKey}`;
  }, [brandsKey, dateFilter]);

  // CRITICAL: Check global cache IMMEDIATELY on mount/filter change to restore data instantly
  // This prevents the "flash of empty state" when navigating back to this page
  const initialCachedData = useMemo(() => {
    if (!cacheKey) return null;
    return getCachedData(cacheKey);
  }, [cacheKey]);

  // Ref to prevent duplicate fetching
  const fetchingRef = useRef(false);
  const cachedDataRef = useRef(null);
  // Debounce timer for progressive loading state updates
  const progressiveUpdateTimerRef = useRef(null);
  // Track the last cacheKey we loaded data for
  const lastLoadedCacheKeyRef = useRef(initialCachedData ? cacheKey : null);

  // Track if we've loaded data for current brands
  const [hasLoadedForBrands, setHasLoadedForBrands] = useState(
    !!initialCachedData
  );

  // Initialize state from cache if available - instant restore on navigation
  const [loading, setLoading] = useState(false);
  const [allTransactions, setAllTransactions] = useState(
    () => initialCachedData?.data || []
  );
  const [totalTransactionCount, setTotalTransactionCount] = useState(
    () => initialCachedData?.count || 0
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [partners, setPartners] = useState([]);
  const [statementHistoryData, setStatementHistoryData] = useState([]);
  const [statementHistoryLoading, setStatementHistoryLoading] = useState(false);
  const statementHistoryRef = useRef(null);

  // Allocation Modal States
  const [paymentAllocationVisible, setPaymentAllocationVisible] =
    useState(false);
  const [paymentTransactionId, setPaymentTransactionId] = useState(null);

  const [creditAllocationVisible, setCreditAllocationVisible] = useState(false);
  const [creditId, setCreditId] = useState(null);

  const [cashAllocationVisible, setCashAllocationVisible] = useState(false);
  const [cashId, setCashId] = useState(null);

  const [overpaymentAllocationVisible, setOverpaymentAllocationVisible] =
    useState(false);
  const [overpaymentId, setOverpaymentId] = useState(null);

  const [overPaymentPageVisible, setOverPaymentPageVisible] = useState(false);
  const [overPaymentTransactionId, setOverPaymentTransactionId] =
    useState(null);

  const [editStatementVisible, setEditStatementVisible] = useState(false);
  const [createStatementVisible, setCreateStatementVisible] = useState(false);
  const [statementId, setStatementId] = useState(null);
  const [uploadStatementVisible, setUploadStatementVisible] = useState(false);

  // Create Modals
  const [createCashModalVisible, setCreateCashModalVisible] = useState(false);
  const [createCreditModalVisible, setCreateCreditModalVisible] =
    useState(false);
  const [editingCreditId, setEditingCreditId] = useState(null);
  const [editingCashId, setEditingCashId] = useState(null);

  // Invoice View Modal
  const [viewInvoiceModalVisible, setViewInvoiceModalVisible] = useState(false);
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);

  // Single-transaction edit modal
  const [editTxnVisible, setEditTxnVisible] = useState(false);
  const [editTxnLoading, setEditTxnLoading] = useState(false);
  const [editTxnId, setEditTxnId] = useState(null);
  const [editTxnForm] = Form.useForm();
  const [editTxnMeta, setEditTxnMeta] = useState(null);
  const [editBankDetails, setEditBankDetails] = useState(null);
  const [banksCache, setBanksCache] = useState([]);

  // AG Grid Ref
  const gridRef = useRef(null);

  // Selected rows for bulk actions
  const [selectedRows, setSelectedRows] = useState([]);
  const onSelectionChanged = useCallback(() => {
    if (gridRef.current) {
      setSelectedRows(gridRef.current.getSelectedRows() || []);
    }
  }, []);

  // Statistics
  const [statistics, setStatistics] = useState({
    totalAllocations: 0,
    pendingAllocations: 0,
    fullyAllocated: 0,
    totalTransactions: 0,
  });

  const totalUnallocated = useMemo(() => {
    return allTransactions.reduce((sum, txn) => {
      const remaining = Math.abs(parseFloat(txn.remainingAmount || 0));
      return sum + remaining;
    }, 0);
  }, [allTransactions]);

  // Background loading state for hybrid loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const backgroundLoadingRef = useRef(false);
  const backgroundAbortRef = useRef(false);

  // Control active tab and partner drill-through
  const [activeTabKey, setActiveTabKey] = useState("transactions");
  const [selectedPartnerForTransactions, setSelectedPartnerForTransactions] =
    useState(null);

  // Unified fetch function for all transaction types - HYBRID LOADING
  // Loads first 1000 records fast, shows them immediately, then loads rest in background
  const fetchAllTransactions = useCallback(
    async (forceRefresh = false, suppressErrors = false) => {
      if (!selectedBrands || selectedBrands.length === 0) {
        setAllTransactions([]);
        setTotalTransactionCount(0);
        return;
      }

      // Check GLOBAL cache first if not forcing refresh
      if (!forceRefresh && cacheKey) {
        const cachedTransactions = getCachedData(cacheKey);
        if (cachedTransactions) {
          setAllTransactions(cachedTransactions.data || []);
          setTotalTransactionCount(cachedTransactions.count || 0);
          setHasLoadedForBrands(true);
          lastLoadedCacheKeyRef.current = cacheKey;
          setLoading(false);
          fetchingRef.current = false;
          return;
        }
      }

      // Check if we already have data for this EXACT cache key
      if (
        !forceRefresh &&
        cacheKey &&
        allTransactions.length > 0 &&
        lastLoadedCacheKeyRef.current === cacheKey
      ) {
        setLoading(false);
        fetchingRef.current = false;
        return;
      }

      // Prevent duplicate simultaneous requests
      if (fetchingRef.current && !forceRefresh) {
        return;
      }

      // Abort any ongoing background loading
      backgroundAbortRef.current = true;
      fetchingRef.current = true;
      setLoading(true);

      // Clear any pending progressive update timer
      if (progressiveUpdateTimerRef.current) {
        clearTimeout(progressiveUpdateTimerRef.current);
      }

      try {
        const brandIds = selectedBrands.map((b) => b.id);
        const brandIdsStr = brandIds.join(",");

        // Helper to format transaction data
        const formatTransaction = (item, transactionType) => {
          const partnerName =
            item.partner?.name ||
            item.partner_name ||
            item.vendor_name ||
            item.partner ||
            "-";
          const partnerId =
            item.partner?.id || item.partner_id || item.vendor_id || null;

          const bankName =
            item.bank?.name ||
            item.bank_name ||
            item.statement?.bank?.name ||
            item.bank ||
            "-";

          const amount = parseFloat(item.amount || item.total_amount || 0);
          const balance =
            item.balance !== null && item.balance !== undefined && item.balance !== ""
              ? parseFloat(item.balance)
              : null;
          const allocatedAmount = parseFloat(
            item.allocated_amount || item.allocated_sum || 0
          );

          const direction = amount >= 0 ? 1 : -1;
          let remainingAmount = amount - allocatedAmount * direction;

          if (
            (amount < 0 && remainingAmount > 0) ||
            (amount >= 0 && remainingAmount < 0)
          ) {
            remainingAmount = 0;
          }

          const percentAllocated =
            amount !== 0 ? (allocatedAmount / Math.abs(amount)) * 100 : 0;

          // Use backend allocation_status if available (includes Overpay, etc.)
          let allocationStatus = "Unallocated";
          if (item.allocation_status?.label) {
            allocationStatus = item.allocation_status.label;
          } else if (item.allocation_status?.type) {
            const typeMap = {
              overpay: "Overpay",
              fully_allocated: "Fully Allocated",
              partially_allocated: "Partially Allocated",
              unallocated: "Unallocated",
            };
            allocationStatus =
              typeMap[item.allocation_status.type] ||
              item.allocation_status.type;
          } else if (percentAllocated >= 99.99) {
            allocationStatus = "Fully Allocated";
          } else if (percentAllocated > 0) {
            allocationStatus = "Partially Allocated";
          }

          const rawDateValue =
            item.date ||
            item.transaction_date ||
            item.issue_date ||
            item.created_at;

          let formattedDate = rawDateValue;
          if (
            formattedDate &&
            typeof formattedDate === "string" &&
            !formattedDate.match(/^[A-Za-z]{3} \d{2}, \d{4}$/)
          ) {
            try {
              formattedDate = new Date(formattedDate).toLocaleDateString(
                "en-US",
                { month: "short", day: "2-digit", year: "numeric" }
              );
            } catch (e) {
              formattedDate = formattedDate || "N/A";
            }
          }

          const rawAllocations =
            item.allocations ||
            item.payment_allocations ||
            item.cash_allocations ||
            item.credit_allocations ||
            [];

          const allocationsArray = (
            Array.isArray(rawAllocations) ? rawAllocations : []
          ).filter((alloc) => alloc !== null);

          const statementId =
            item.statement_id ||
            (item.statement && typeof item.statement === "object"
              ? item.statement.id
              : item.statement) ||
            item.statementId ||
            null;

          // Determine detail (DEBIT/CREDIT) - check explicit field or infer from amount
          // For overpayments and credits, backend may send transaction_detail or detail
          let detailValue = item.detail || item.transaction_detail;
          if (!detailValue) {
            // Infer from amount:
            // Positive = DEBIT (money going out, e.g., you paid a vendor)
            // Negative = CREDIT (money coming in, e.g., received from customer)
            detailValue = amount >= 0 ? "DEBIT" : "CREDIT";
          }

          // Capture overpayment info for transactions that created overpayments
          const overpaymentId = item.overpayment_id || item.overpayment?.id || null;
          const overpaymentNumber = item.overpayment_number || 
            (item.overpayment?.id ? `OP-${item.overpayment.id}` : null) ||
            (overpaymentId ? `OP-${overpaymentId}` : null);
          
          // Debug: Log transactions with overpay status
          if (item.active_overpayment_amount > 0 || allocationStatus === "Overpay") {
            console.warn('[DEBUG OVERPAY] Transaction:', item.id, {
              'item.overpayment_id': item.overpayment_id,
              'item.overpayment_number': item.overpayment_number,
              'item.overpayment': item.overpayment,
              'computed overpaymentId': overpaymentId,
              'computed overpaymentNumber': overpaymentNumber,
              'allocationStatus': allocationStatus,
              'WILL ADD SYNTHETIC': allocationStatus === "Overpay" && overpaymentId ? 'YES' : 'NO'
            });
          }

          const formatted = {
            ...item,
            id:
              item.id ||
              `${transactionType.toLowerCase().replace(" ", "-")}-${item.id}`,
            originalId: item.id,
            type: transactionType,
            number:
              item.number ||
              item.transaction_number ||
              item.overpayment_number ||
              item.cash_number ||
              item.credit_number ||
              item.invoice_number ||
              `ID-${item.id}`,
            date: formattedDate || item.date || "N/A",
            rawDate: rawDateValue || null,
            partner: partnerName,
            partnerId: partnerId ? Number(partnerId) : null,
            bank: bankName,
            detail: detailValue,
            amount: amount,
            balance: Number.isNaN(balance) ? null : balance,
            allocatedAmount: allocatedAmount,
            remainingAmount: remainingAmount,
            percentAllocated: percentAllocated,
            allocationStatus: allocationStatus,
            statementId: statementId,
            statement_id: statementId,
            // Overpayment info for "Overpay" status transactions
            overpaymentId: overpaymentId,
            overpaymentNumber: overpaymentNumber,
          };

          // For "Overpay" transactions, add the overpayment as an allocation for display
          let finalAllocations = [...allocationsArray];
          if (allocationStatus === "Overpay" && overpaymentId) {
            // Add a synthetic allocation entry to show the overpayment in "Allocated To" column
            finalAllocations.push({
              id: `op-created-${overpaymentId}`,
              overpayment_id: overpaymentId,
              overpayment_number: overpaymentNumber,
              allocation_type: 'created_overpayment',
              amount: item.overpayment_amount || item.remaining_amount || 0,
            });
          }

          formatted.allocations = finalAllocations;
          formatted.invoiceCount =
            item.invoiceCount ||
            allocationsArray.filter((a) => a && (a.invoice_id || a.invoice))
              .length;

          return formatted;
        };

        // Build common filters
        const baseFilters = {
          brand: brandIdsStr,
          include_allocations: "true",
          start_date: dateFilter?.[0]?.format("YYYY-MM-DD"),
          end_date: dateFilter?.[1]?.format("YYYY-MM-DD"),
        };

        // For credits, only show approved ones in TransactionsAllocations
        const creditFilters = {
          ...baseFilters,
          approval_status: "approved",
        };

        // ========== PHASE 1: Quick first page load ==========
        // Fetch first page of each type in parallel - this is FAST
        const firstPagePromises = [
          getFirstPageBankStatements(baseFilters).catch(() => ({
            results: [],
            totalCount: 0,
            hasMore: false,
          })),
          getFirstPageOverpayments({
            brand: brandIdsStr,
            start_date: baseFilters.start_date,
            end_date: baseFilters.end_date,
          }).catch(() => ({ results: [], totalCount: 0, hasMore: false })),
          getFirstPageCash(baseFilters).catch(() => ({
            results: [],
            totalCount: 0,
            hasMore: false,
          })),
          getFirstPageCredits(creditFilters).catch(() => ({
            results: [],
            totalCount: 0,
            hasMore: false,
          })),
        ];

        const firstPageResults = await Promise.all(firstPagePromises);

        // Process first page results
        const initialTransactions = [];
        let estimatedTotalCount = 0;
        const typesWithMore = [];

        // Bank Statements
        if (firstPageResults[0]?.results?.length > 0) {
          initialTransactions.push(
            ...firstPageResults[0].results.map((item) =>
              formatTransaction(item, "Bank Statement")
            )
          );
          estimatedTotalCount +=
            firstPageResults[0].totalCount ||
            firstPageResults[0].results.length;
          if (firstPageResults[0].hasMore) typesWithMore.push("transactions");
        }

        // Overpayments
        if (firstPageResults[1]?.results?.length > 0) {
          initialTransactions.push(
            ...firstPageResults[1].results.map((item) =>
              formatTransaction(item, "Overpayment")
            )
          );
          estimatedTotalCount +=
            firstPageResults[1].totalCount ||
            firstPageResults[1].results.length;
          if (firstPageResults[1].hasMore) typesWithMore.push("overpayments");
        }

        // Cash
        if (firstPageResults[2]?.results?.length > 0) {
          initialTransactions.push(
            ...firstPageResults[2].results.map((item) =>
              formatTransaction(item, "Cash")
            )
          );
          estimatedTotalCount +=
            firstPageResults[2].totalCount ||
            firstPageResults[2].results.length;
          if (firstPageResults[2].hasMore) typesWithMore.push("cash");
        }

        // Credits
        if (firstPageResults[3]?.results?.length > 0) {
          initialTransactions.push(
            ...firstPageResults[3].results.map((item) =>
              formatTransaction(item, "Credit")
            )
          );
          estimatedTotalCount +=
            firstPageResults[3].totalCount ||
            firstPageResults[3].results.length;
          if (firstPageResults[3].hasMore) typesWithMore.push("credits");
        }

        // Sort: banks first, then by date (newest first)
        const sortedInitial = [...initialTransactions].sort((a, b) => {
          if (a.type === "Bank Statement" && b.type !== "Bank Statement")
            return -1;
          if (a.type !== "Bank Statement" && b.type === "Bank Statement")
            return 1;
          const dateA = a.rawDate || a.transaction_date || "";
          const dateB = b.rawDate || b.transaction_date || "";
          if (dateA && dateB) return new Date(dateB) - new Date(dateA);
          return 0;
        });

        // ========== SHOW FIRST PAGE IMMEDIATELY ==========
        setAllTransactions(sortedInitial);
        setTotalTransactionCount(estimatedTotalCount);
        setHasLoadedForBrands(true);
        lastLoadedCacheKeyRef.current = cacheKey;
        setLoading(false);
        fetchingRef.current = false;

        // ========== PHASE 2: Background load remaining data ==========
        if (typesWithMore.length > 0) {
          // Reset abort flag for new background loading
          backgroundAbortRef.current = false;
          backgroundLoadingRef.current = true;
          setIsLoadingMore(true);

          // Use setTimeout to let the UI render first
          setTimeout(async () => {
            try {
              // Fetch ALL data (including first page again for simplicity)
              const fullFetchPromises = [
                getAllBankStatementsParallel(baseFilters).catch(() => ({
                  results: [],
                })),
                getAllPartnerOverpayments({
                  brand: brandIdsStr,
                  start_date: baseFilters.start_date,
                  end_date: baseFilters.end_date,
                }).catch(() => ({ results: [], data: [] })),
                getAllCash(baseFilters).catch(() => ({
                  results: [],
                  data: [],
                })),
                getAllCredits(creditFilters).catch(() => ({
                  results: [],
                  data: [],
                })),
              ];

              const fullResults = await Promise.all(fullFetchPromises);

              // Check if we should abort
              if (backgroundAbortRef.current) {
                backgroundLoadingRef.current = false;
                setIsLoadingMore(false);
                return;
              }

              // Combine all full results
              const allTransactionsData = [];

              // Bank Statements
              const bankResults = fullResults[0]?.results || [];
              allTransactionsData.push(
                ...bankResults.map((item) =>
                  formatTransaction(item, "Bank Statement")
                )
              );

              // Overpayments
              const overpaymentResults =
                fullResults[1]?.results || fullResults[1]?.data || [];
              allTransactionsData.push(
                ...overpaymentResults.map((item) =>
                  formatTransaction(item, "Overpayment")
                )
              );

              // Cash
              const cashResults =
                fullResults[2]?.results || fullResults[2]?.data || [];
              allTransactionsData.push(
                ...cashResults.map((item) => formatTransaction(item, "Cash"))
              );

              // Credits
              const creditResults =
                fullResults[3]?.results || fullResults[3]?.data || [];
              allTransactionsData.push(
                ...creditResults.map((item) =>
                  formatTransaction(item, "Credit")
                )
              );

              // Check abort again before updating state
              if (backgroundAbortRef.current) {
                backgroundLoadingRef.current = false;
                setIsLoadingMore(false);
                return;
              }

              // Sort all data
              const sortedAll = [...allTransactionsData].sort((a, b) => {
                if (a.type === "Bank Statement" && b.type !== "Bank Statement")
                  return -1;
                if (a.type !== "Bank Statement" && b.type === "Bank Statement")
                  return 1;
                const dateA = a.rawDate || a.transaction_date || "";
                const dateB = b.rawDate || b.transaction_date || "";
                if (dateA && dateB) return new Date(dateB) - new Date(dateA);
                return 0;
              });

              // Update state with full data
              setAllTransactions(sortedAll);
              setTotalTransactionCount(sortedAll.length);

              // Cache the full data
              if (cacheKey) {
                setCachedData(
                  cacheKey,
                  {
                    data: sortedAll,
                    count: sortedAll.length,
                  },
                  CacheTTL.MEDIUM
                );
              }
            } catch (err) {
              console.error("Background loading error:", err);
            } finally {
              backgroundLoadingRef.current = false;
              setIsLoadingMore(false);
            }
          }, 100); // Small delay to let UI render
        } else {
          // No more data to load - cache what we have
          if (cacheKey) {
            setCachedData(
              cacheKey,
              {
                data: sortedInitial,
                count: sortedInitial.length,
              },
              CacheTTL.MEDIUM
            );
          }
        }
      } catch (err) {
        console.error("Fetch error:", err);
        if (!suppressErrors) {
          message.error("Failed to load transactions");
        }
        setAllTransactions([]);
        setTotalTransactionCount(0);
        setLoading(false);
      } finally {
        fetchingRef.current = false;
      }
    },
    [
      selectedBrands,
      selectedTypes,
      dateFilter,
      cacheKey,
      allTransactions.length,
    ]
  );

  // NOTE: Filter, sort, and column state persistence is now handled automatically
  // by AgGridWithColumnState component via the useColumnState hook.
  // State is saved to localStorage with user-specific keys on every change
  // and restored when the grid is ready.

  // Helper to clear cache and refresh - state is automatically preserved by AgGridWithColumnState
  const clearCacheAndRefresh = useCallback(
    (suppressErrors = false) => {
      // NOTE: Filter, sort, and column state are automatically saved by AgGridWithColumnState
      // on every change, so we don't need to manually save them before refreshing.
      // The state will be automatically restored when the grid re-renders with new data.

      if (cacheKey) {
        invalidateCacheByPrefix("transactions_");
        setHasLoadedForBrands(false);
      }
      fetchAllTransactions(true, suppressErrors);
    },
    [cacheKey, fetchAllTransactions]
  );

  // Handle pagination changes - client-side only, NO REFETCH
  // This function ONLY updates pagination state - it does NOT trigger any data fetching
  const handlePaginationChange = useCallback((newPage, newPageSize) => {
    // Only update state if values actually changed - prevents unnecessary re-renders
    // CRITICAL: This does NOT call fetchAllTransactions or clearCacheAndRefresh
    // Pagination is purely client-side - the grid handles it internally
    setCurrentPage((prev) => {
      if (prev !== newPage) {
        return newPage;
      }
      return prev;
    });
    setPageSize((prev) => {
      if (prev !== newPageSize) {
        return newPageSize;
      }
      return prev;
    });
  }, []);

  // Fetch statistics from the backend
  const fetchStatistics = useCallback(
    async (force = false) => {
      if (!brandsKey) return;

      const filters = {
        brand_ids: brandsKey.split(","),
      };
      if (dateFilter && dateFilter.length === 2) {
        filters.start_date = dateFilter[0].format("YYYY-MM-DD");
        filters.end_date = dateFilter[1].format("YYYY-MM-DD");
      }

      const fetchKey = `stats-${brandsKey}-${filters.start_date || "none"}-${
        filters.end_date || "none"
      }`;

      // Check cache first if not forcing refresh
      const statsCacheKey = `transactions-allocations-stats-${fetchKey}`;
      if (
        !force &&
        cachedDataRef.current &&
        cachedDataRef.current[statsCacheKey]
      ) {
        const cached = cachedDataRef.current[statsCacheKey];
        const cacheAge = Date.now() - (cached.timestamp || 0);
        // Cache valid for 2 minutes
        if (cacheAge < 2 * 60 * 1000) {
          setStatistics(cached.data || statistics);
          return;
        }
      }

      try {
        const stats = await getTransactionsAllocationsStatistics(filters);
        setStatistics(stats);

        // Cache the results
        if (statsCacheKey) {
          if (!cachedDataRef.current) {
            cachedDataRef.current = {};
          }
          cachedDataRef.current[statsCacheKey] = {
            data: stats,
            timestamp: Date.now(),
          };
        }
      } catch (error) {
        console.error("Error fetching statistics:", error);
        message.error("Failed to load statistics.");
      }
    },
    [brandsKey, dateFilter, statistics]
  );

  // Keep ref updated with latest fetchStatistics
  const fetchStatisticsRef = useRef(null);
  useEffect(() => {
    fetchStatisticsRef.current = fetchStatistics;
  }, [fetchStatistics]);

  // Fetch Statistics - with longer delay to not compete with main data
  useEffect(() => {
    if (!brandsKey) return;

    // Longer delay to let main data fetch complete first
    const timeoutId = setTimeout(() => {
      if (fetchStatisticsRef.current) {
        fetchStatisticsRef.current();
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [brandsKey, dateFilter]);

  useEffect(() => {
    if (!brandsKey || !selectedBrands || selectedBrands.length === 0) {
      setPartners([]);
      return;
    }

    // Delay partners fetch to not compete with main data fetch
    const timeoutId = setTimeout(() => {
      const fetchPartners = async () => {
        try {
          // Fetch partners for all selected brands
          const allPartnersPromises = selectedBrands.map((brand) =>
            getAllPartners({ brand: brand.id })
          );

          const allPartnersResults = await Promise.all(allPartnersPromises);

          // Combine and deduplicate partners by ID and by name
          const partnersMap = new Map();
          const seenNames = new Set();
          allPartnersResults.forEach((result) => {
            const partnersList = Array.isArray(result.data) ? result.data : [];
            partnersList.forEach((partner) => {
              // Skip if we already have this ID
              if (partnersMap.has(partner.id)) return;
              // Skip if we already have this name (for duplicate partner records)
              const normalizedName = (partner.name || "").toLowerCase().trim();
              if (seenNames.has(normalizedName)) return;
              partnersMap.set(partner.id, partner);
              seenNames.add(normalizedName);
            });
          });

          setPartners(Array.from(partnersMap.values()));
        } catch (error) {
          console.error("Error fetching partners:", error);
          setPartners([]);
        }
      };

      fetchPartners();
    }, 1500); // 1.5 second delay to let main data load first

    return () => clearTimeout(timeoutId);
  }, [brandsKey, selectedBrands]);

  // Fetch Statement History
  const fetchStatementHistory = useCallback(async () => {
    if (!brandsKey || !selectedBrands || selectedBrands.length === 0) return;

    setStatementHistoryLoading(true);
    try {
      // Fetch statement history for all selected brands
      const allHistoryPromises = selectedBrands.map((brand) =>
        getAllStatements({ brand: brand.id })
      );

      const allHistoryResults = await Promise.all(allHistoryPromises);

      // Combine all statement history
      const combinedHistory = [];
      allHistoryResults.forEach((response) => {
        if (response.results) {
          combinedHistory.push(...response.results);
        }
      });

      setStatementHistoryData(combinedHistory);
    } catch (error) {
      console.error("Error fetching statement history:", error);
      message.error("Failed to fetch statement history");
      setStatementHistoryData([]);
    } finally {
      setStatementHistoryLoading(false);
    }
  }, [brandsKey, selectedBrands]);

  // Keep refs updated with latest functions
  const fetchAllTransactionsRef = useRef(fetchAllTransactions);
  const fetchStatementHistoryRef = useRef(fetchStatementHistory);

  useEffect(() => {
    fetchAllTransactionsRef.current = fetchAllTransactions;
  }, [fetchAllTransactions]);

  useEffect(() => {
    fetchStatementHistoryRef.current = fetchStatementHistory;
  }, [fetchStatementHistory]);

  // Initial data fetch - only when brands, types, or date filter changes (NOT pagination)
  // CRITICAL: This effect does NOT depend on currentPage or pageSize
  // Pagination changes do NOT trigger this effect - data is fetched once and kept in memory
  // The cacheKey changes when filters change, which will trigger a fetch, but pagination doesn't change cacheKey
  useEffect(() => {
    if (!brandsKey || !cacheKey) {
      setAllTransactions([]);
      setStatementHistoryData([]);
      setHasLoadedForBrands(false);
      return;
    }

    // Check if force refresh is needed (e.g., global filter changed)
    const needsForceRefresh = forceRefreshRef.current;
    if (needsForceRefresh) {
      forceRefreshRef.current = false;
    }

    // CRITICAL: Check GLOBAL cache IMMEDIATELY - use it if available (unless force refresh)
    if (!needsForceRefresh) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData && cachedData.data?.length > 0) {
        setAllTransactions(cachedData.data);
        setTotalTransactionCount(cachedData.count || cachedData.data.length);
        setHasLoadedForBrands(true);
        lastLoadedCacheKeyRef.current = cacheKey;
        setLoading(false);
        return;
      }

      // CRITICAL: If we already have data in state for this cacheKey, DON'T refetch
      // This prevents the refetch when navigating back to the page
      if (
        allTransactions.length > 0 &&
        lastLoadedCacheKeyRef.current === cacheKey
      ) {
        return;
      }
    }

    // Longer delay to batch multiple rapid changes and avoid request flooding
    const timeoutId = setTimeout(() => {
      // Only fetch if not already fetching AND no cached data
      if (!fetchingRef.current && fetchAllTransactionsRef.current) {
        fetchAllTransactionsRef.current(needsForceRefresh, false);
      }
      // Delay statement history fetch to avoid concurrent requests
      setTimeout(() => {
        if (fetchStatementHistoryRef.current) {
          fetchStatementHistoryRef.current();
        }
      }, 500);
    }, 200);

    return () => clearTimeout(timeoutId);
    // CRITICAL: Only depend on cacheKey (which includes brands, types, date) - NOT pagination
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, brandsKey]); // Using cacheKey instead of individual filters - it already includes all filter dependencies

  // Handle Create Cash Payment
  const showCreateCashModal = () => {
    setEditingCashId(null);
    setCreateCashModalVisible(true);
  };

  const handleCreateCashSuccess = () => {
    setCreateCashModalVisible(false);
    setEditingCashId(null);
    clearCacheAndRefresh();
  };

  const handleCreateCashCancel = () => {
    setCreateCashModalVisible(false);
    setEditingCashId(null);
  };

  // Handle Create Credit Note
  const showCreateCreditModal = () => {
    setEditingCreditId(null);
    setCreateCreditModalVisible(true);
  };

  const handleCreateCreditSuccess = (response) => {
    setCreateCreditModalVisible(false);
    setEditingCreditId(null);
    // Note: Success message is already shown in CreateCreditModal, no need to show it again
    
    // Determine credit type from response (response.data or response itself)
    const creditData = response?.data || response;
    const creditType = creditData?.credit_type || "bill";
    
    // Invalidate cache and dispatch event based on credit type
    if (creditType === "bill") {
      invalidateCacheByPrefix("bill_credits_");
      invalidateCacheByPrefix("bills_");
      invalidateCacheByPrefix("payable_statistics_");
      // Dispatch event to notify AccountsPayable to refresh
      window.dispatchEvent(new CustomEvent("creditNoteCreated", { detail: { creditType: "bill" } }));
    } else {
      invalidateCacheByPrefix("invoice_credits_");
      invalidateCacheByPrefix("invoices_");
      invalidateCacheByPrefix("receivable_statistics_");
      // Dispatch event to notify AccountsReceivable to refresh
      window.dispatchEvent(new CustomEvent("creditNoteCreated", { detail: { creditType: "invoice" } }));
    }
    
    clearCacheAndRefresh();
  };

  const handleCreateCreditCancel = () => {
    setCreateCreditModalVisible(false);
    setEditingCreditId(null);
  };

  // Filter data based on selected types
  // Filtering is now done on the backend via the unified endpoint
  // This is just for client-side display (no additional filtering needed)
  // CRITICAL: Use a ref to store filtered data to prevent re-filtering on every render
  const filteredTransactionsRef = useRef([]);
  const filteredTransactions = useMemo(() => {
    const filtered = allTransactions.filter((tx) => {
      if (tx.type === "Bank Statement" && !selectedTypes.bankStatement)
        return false;
      if (tx.type === "Overpayment" && !selectedTypes.overpayment) return false;
      if (tx.type === "Cash" && !selectedTypes.cash) return false;
      if (tx.type === "Credit" && !selectedTypes.credit) return false;
      return true;
    });
    // Update ref to maintain stable reference
    filteredTransactionsRef.current = filtered;
    return filtered;
  }, [allTransactions, selectedTypes]);

  // Handle type filter change
  const handleTypeFilterChange = (type, checked) => {
    setSelectedTypes((prev) => ({
      ...prev,
      [type]: checked,
    }));
  };

  // Handle URL parameters to open allocation modal
  const urlParamProcessedRef = useRef(false);

  useEffect(() => {
    const transactionId = searchParams.get("transaction");
    const txnType = searchParams.get("txnType");
    if (!transactionId || transactionId === "[object Object]") {
      urlParamProcessedRef.current = false;
      return;
    }

    if (urlParamProcessedRef.current) {
      return;
    }

    // ID may be like "transaction-25200" or just "25200" — extract the number
    const numericMatch = String(transactionId).match(/(\d+)$/);
    if (!numericMatch) return;
    const numericId = parseInt(numericMatch[1], 10);

    urlParamProcessedRef.current = true;
    setSearchParams({}, { replace: true });

    // If txnType is provided, open the correct modal directly
    if (txnType) {
      switch (txnType) {
        case "bank_statement":
          setPaymentTransactionId(numericId);
          setPaymentAllocationVisible(true);
          return;
        case "cash":
          setCashId(numericId);
          setCashAllocationVisible(true);
          return;
        case "credit":
          setCreditId(numericId);
          setCreditAllocationVisible(true);
          return;
        case "overpayment":
          setOverpaymentId(numericId);
          setOverpaymentAllocationVisible(true);
          return;
        default:
          break;
      }
    }

    // Fallback: try to find the transaction in allTransactions to determine type
    const transaction = allTransactions.find(
      (tx) => String(tx.originalId || tx.id) === String(transactionId)
    );

    if (transaction) {
      if (transaction.type === "Bank Statement") {
        setPaymentTransactionId(numericId);
        setPaymentAllocationVisible(true);
      } else if (transaction.type === "Cash") {
        setCashId(numericId);
        setCashAllocationVisible(true);
      } else if (transaction.type === "Credit") {
        setCreditId(numericId);
        setCreditAllocationVisible(true);
      } else if (transaction.type === "Overpayment") {
        setOverpaymentId(numericId);
        setOverpaymentAllocationVisible(true);
      }
    } else {
      // Default to PaymentAllocation for bank statements (most common)
      setPaymentTransactionId(numericId);
      setPaymentAllocationVisible(true);
    }
  }, [searchParams, allTransactions, setSearchParams]);

  // Action handlers - these will open the respective allocation modals
  const handleAffectPayment = useCallback((transactionId) => {
    setPaymentTransactionId(transactionId);
    setPaymentAllocationVisible(true);
  }, []);

  const handleAffectOverpayment = useCallback((id, rowData = null) => {
    // If it's a transaction (Bank Statement), open OverPaymentPage modal to create overpayment
    if (rowData && rowData.type === "Bank Statement") {
      setOverPaymentTransactionId(id);
      setOverPaymentPageVisible(true);
    } else {
      // It's already an overpayment ID, open allocation modal directly
      setOverpaymentId(id);
      setOverpaymentAllocationVisible(true);
    }
  }, []);

  const handleOverPaymentPageSuccess = (overpaymentId) => {
    setOverPaymentPageVisible(false);
    setOverPaymentTransactionId(null);
    clearCacheAndRefresh();

    // If overpayment was created, open allocation modal
    if (overpaymentId) {
      setOverpaymentId(overpaymentId);
      setOverpaymentAllocationVisible(true);
    }
  };

  const handleOverPaymentPageCancel = () => {
    setOverPaymentPageVisible(false);
    setOverPaymentTransactionId(null);
    // Don't refresh on cancel - only refresh on success
  };

  // Delete overpayment (separate path from bank transaction delete)
  const handleDeleteOverpayment = useCallback(
    (rowData) => {
      const allocatedAmount = parseFloat(rowData?.allocatedAmount || 0);
      const allocationsArray = Array.isArray(rowData?.allocations)
        ? rowData.allocations
        : [];
      const allocationStatus = rowData?.allocationStatus;

      const hasAllocations =
        allocatedAmount > 0 ||
        allocationsArray.length > 0 ||
        allocationStatus === "Fully Allocated" ||
        allocationStatus === "Partially Allocated";

      if (hasAllocations) {
        // Open allocation modal to unallocate first
        const overpaymentId = rowData?.originalId || rowData?.id;
        if (!overpaymentId) {
          message.error("Cannot open allocations: Overpayment ID not found");
          return;
        }
        setOverpaymentId(overpaymentId);
        setOverpaymentAllocationVisible(true);
        return;
      }

      // If no allocations, proceed with delete
      const overpaymentId = rowData?.originalId || rowData?.id;
      if (!overpaymentId) {
        message.error("Cannot delete: Overpayment ID not found");
        return;
      }

      Modal.confirm({
        title: "Delete Overpayment",
        content: `Are you sure you want to delete Overpayment #${
          rowData?.number || overpaymentId
        }?`,
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await deleteOverpayment(overpaymentId);
            message.success("Overpayment deleted successfully");

            // Remove from local state immediately (preserves filters)
            setAllTransactions((prev) =>
              prev.filter((t) => {
                const tId = t.originalId || t.id;
                return String(tId) !== String(overpaymentId);
              })
            );

            // Invalidate cache for future refresh
            invalidateCacheByPrefix("overpayments_");
            invalidateCacheByPrefix("transactions_");
          } catch (error) {
            console.error("Error deleting overpayment:", error);
            const backendMsg =
              error?.response?.data?.detail ||
              error?.response?.data?.message ||
              (typeof error?.response?.data === "string"
                ? error.response.data
                : null);

            if (backendMsg && backendMsg.toLowerCase().includes("allocation")) {
              Modal.error({
                title: "Cannot Delete Overpayment - Has Allocations",
                content: (
                  <div>
                    <p>{backendMsg}</p>
                    <Button
                      type="primary"
                      style={{ marginTop: 16 }}
                      onClick={() => {
                        Modal.destroyAll();
                        setOverpaymentId(overpaymentId);
                        setOverpaymentAllocationVisible(true);
                      }}
                    >
                      Go to Allocations
                    </Button>
                  </div>
                ),
              });
              return;
            }

            Modal.error({
              title: "Cannot Delete Overpayment",
              content:
                backendMsg ||
                "An error occurred while deleting the overpayment.",
            });
          }
        },
      });
    },
    [fetchAllTransactions, clearCacheAndRefresh]
  );

  const handleAffectCredit = useCallback((creditId) => {
    setCreditId(creditId);
    setCreditAllocationVisible(true);
  }, []);

  const handleAffectCash = useCallback((cashId) => {
    setCashId(cashId);
    setCashAllocationVisible(true);
  }, []);

  const handleEditStatement = useCallback((statementId) => {
    setStatementId(statementId);
    setEditStatementVisible(true);
  }, []);

  // Track statement id for the transaction being edited (needed by backend)
  const [editTxnStatementId, setEditTxnStatementId] = useState(null);
  // Track statement header details to mirror Create Statement layout
  const [editStatementDetails, setEditStatementDetails] = useState(null);
  // Editable header draft (bank + balances)
  const [editHeaderDraft, setEditHeaderDraft] = useState({
    bankId: null,
    opening_balance: 0,
    closing_balance: 0,
  });

  // Preload banks for current brand to show bank details in the edit modal
  // Delay this fetch to avoid request flooding on initial load
  useEffect(() => {
    const firstBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;
    if (!firstBrand) return;

    // Delay banks fetch to not compete with main data fetch
    const timeoutId = setTimeout(() => {
      getAllBanks({ brand: firstBrand.id })
        .then((resp) => {
          const list = resp?.data || resp || [];
          setBanksCache(Array.isArray(list) ? list : []);
        })
        .catch(() => setBanksCache([]));
    }, 1000); // 1 second delay

    return () => clearTimeout(timeoutId);
  }, [brandsKey, selectedBrands]);

  const handleEditTransaction = useCallback(
    (rowData) => {
      // Open single-transaction editor instead of whole statement
      const txnId = rowData?.originalId || rowData?.id;
      if (!txnId) {
        message.error("Cannot edit: Transaction ID not found");
        return;
      }
      // Prefill bank name from grid row if present for immediate UI feedback
      try {
        const rowBankName =
          rowData?.bank_name || rowData?.bank || rowData?.bankName || "";
        if (rowBankName) {
          setEditTxnMeta((prev) => ({
            ...(prev || {}),
            bank_name: rowBankName,
            duplicate_status: (prev && prev.duplicate_status) || "NEW_ENTRY",
          }));
          setEditBankDetails((prev) => prev || { name: rowBankName });
          // bank id resolution not required for read-only header; details will hydrate from statement
        }
      } catch {
        // ignore
      }
      setEditTxnLoading(true);
      getTransactionById(txnId)
        .then((data) => {
          setEditTxnId(txnId);
          setEditTxnMeta({
            duplicate_status: data.duplicate_status || "NEW_ENTRY",
            bank_name: data?.statement?.bank?.name || "",
          });
          // If bank name is missing but we have a statement id, fetch the statement to resolve bank
          const statementId =
            (data?.statement &&
              (typeof data.statement === "object"
                ? data.statement.id
                : data.statement)) ||
            data?.statement_id ||
            null;
          setEditTxnStatementId(statementId || null);
          // If the transaction already carries full bank info, hydrate immediately
          try {
            const inlineBank =
              (data?.statement && typeof data.statement === "object"
                ? data.statement.bank
                : null) || null;
            if (inlineBank && typeof inlineBank === "object") {
              setEditBankDetails({
                name: inlineBank.name || "",
                account_number: inlineBank.account_number || "",
                account_balance:
                  typeof inlineBank.account_balance === "number"
                    ? inlineBank.account_balance
                    : parseFloat(inlineBank.account_balance || 0),
                latest_statement_closing_balance:
                  typeof inlineBank.latest_statement_closing_balance ===
                  "number"
                    ? inlineBank.latest_statement_closing_balance
                    : parseFloat(
                        inlineBank.latest_statement_closing_balance || 0
                      ),
              });
            }
            // Inline statement balances if present
            if (data?.statement && typeof data.statement === "object") {
              setEditStatementDetails({
                opening_balance:
                  typeof data.statement.opening_balance === "number"
                    ? data.statement.opening_balance
                    : parseFloat(data.statement.opening_balance || 0),
                closing_balance:
                  typeof data.statement.closing_balance === "number"
                    ? data.statement.closing_balance
                    : parseFloat(data.statement.closing_balance || 0),
                bank_name: inlineBank?.name || "",
              });
            }
          } catch {
            // ignore
          }
          if (statementId) {
            getStatementById(statementId)
              .then((stmt) => {
                const resolvedBankName = stmt?.bank?.name || "";
                if (resolvedBankName) {
                  setEditTxnMeta((prev) => ({
                    ...prev,
                    bank_name: resolvedBankName,
                  }));
                }
                // Save statement balances for header
                const stmtDetails = {
                  opening_balance:
                    typeof stmt?.opening_balance === "number"
                      ? stmt.opening_balance
                      : parseFloat(stmt?.opening_balance || 0),
                  closing_balance:
                    typeof stmt?.closing_balance === "number"
                      ? stmt.closing_balance
                      : parseFloat(stmt?.closing_balance || 0),
                  bank_name: resolvedBankName,
                };
                setEditStatementDetails(stmtDetails);
                setEditHeaderDraft({
                  bankId:
                    (stmt?.bank &&
                      (typeof stmt.bank === "object"
                        ? stmt.bank.id
                        : stmt.bank)) ||
                    null,
                  opening_balance: stmtDetails.opening_balance ?? 0,
                  closing_balance: stmtDetails.closing_balance ?? 0,
                });
                // Also try to hydrate header details if bank cache contains this bank
                const bankId =
                  (stmt?.bank &&
                    (typeof stmt.bank === "object"
                      ? stmt.bank.id
                      : stmt.bank)) ||
                  null;
                // Prefer direct stmt.bank details if available
                if (stmt?.bank && typeof stmt.bank === "object") {
                  setEditBankDetails({
                    name: stmt.bank.name || "",
                    account_number: stmt.bank.account_number || "",
                    account_balance:
                      typeof stmt.bank.account_balance === "number"
                        ? stmt.bank.account_balance
                        : parseFloat(stmt.bank.account_balance || 0),
                    latest_statement_closing_balance:
                      typeof stmt.bank.latest_statement_closing_balance ===
                      "number"
                        ? stmt.bank.latest_statement_closing_balance
                        : parseFloat(
                            stmt.bank.latest_statement_closing_balance || 0
                          ),
                  });
                } else if (bankId && banksCache.length > 0) {
                  const b = banksCache.find((bk) => bk.id === bankId);
                  if (b) {
                    setEditBankDetails({
                      name: b.name || "",
                      account_number: b.account_number || "",
                      account_balance:
                        typeof b.account_balance === "number"
                          ? b.account_balance
                          : parseFloat(b.account_balance || 0),
                      latest_statement_closing_balance:
                        typeof b.latest_statement_closing_balance === "number"
                          ? b.latest_statement_closing_balance
                          : parseFloat(b.latest_statement_closing_balance || 0),
                    });
                  } else {
                    // Fallback: fetch bank by id to guarantee header hydration
                    getBankById(bankId)
                      .then((resp) => {
                        const bank = resp?.data || resp;
                        if (bank) {
                          setEditBankDetails({
                            name: bank.name || "",
                            account_number: bank.account_number || "",
                            account_balance:
                              typeof bank.account_balance === "number"
                                ? bank.account_balance
                                : parseFloat(bank.account_balance || 0),
                            latest_statement_closing_balance:
                              typeof bank.latest_statement_closing_balance ===
                              "number"
                                ? bank.latest_statement_closing_balance
                                : parseFloat(
                                    bank.latest_statement_closing_balance || 0
                                  ),
                          });
                        }
                      })
                      .catch(() => {
                        // leave as-is
                      });
                  }
                }
              })
              .catch(() => {
                // Fallback: ensure header shows at least N/A instead of failing lint
                setEditBankDetails((prev) => prev || null);
              });
          }
          // Resolve bank details from cache if possible
          try {
            const bankId =
              (data?.statement?.bank &&
                (typeof data.statement.bank === "object"
                  ? data.statement.bank.id
                  : data.statement.bank)) ||
              null;
            if (bankId && banksCache.length > 0) {
              const b = banksCache.find((bk) => bk.id === bankId);
              if (b) {
                setEditBankDetails({
                  name: b.name || "",
                  account_number: b.account_number || "",
                  account_balance:
                    typeof b.account_balance === "number"
                      ? b.account_balance
                      : parseFloat(b.account_balance || 0),
                  latest_statement_closing_balance:
                    typeof b.latest_statement_closing_balance === "number"
                      ? b.latest_statement_closing_balance
                      : parseFloat(b.latest_statement_closing_balance || 0),
                });
              } else {
                setEditBankDetails(null);
              }
            } else {
              setEditBankDetails(null);
            }
          } catch {
            setEditBankDetails(null);
          }
          // Pre-fill form
          // Ensure description is a string, not an object
          const descriptionValue =
            typeof data.description === "string"
              ? data.description
              : typeof data.description === "object" &&
                data.description?.description
              ? data.description.description
              : data.description || "";

          editTxnForm.setFieldsValue({
            transaction_date: data.transaction_date
              ? dayjs(data.transaction_date)
              : null,
            description: descriptionValue,
            detail: data.detail || "DEBIT",
            type: data.type || "ACH_DEBIT",
            amount: Number(data.amount || 0),
            reference: data.reference || "",
          });
          setEditTxnVisible(true);
        })
        .catch(() => {
          message.error("Failed to load transaction");
        })
        .finally(() => setEditTxnLoading(false));
    },
    [banksCache, editTxnForm]
  );

  const handleDeleteTransaction = useCallback(async (rowData) => {
    // If allocated, route to allocation modal to unallocate first
    const hasAllocations =
      (rowData?.allocatedAmount || 0) > 0 ||
      (Array.isArray(rowData?.allocations) && rowData.allocations.length > 0) ||
      rowData?.allocationStatus === "Overpaid" ||
      rowData?.allocationStatus === "Fully Allocated" ||
      rowData?.allocationStatus === "Partially Allocated" ||
      rowData?.hasAllocations === true;

    if (hasAllocations) {
      const txnId = rowData?.originalId || rowData?.id;
      if (!txnId) {
        message.error("Cannot open allocations: Transaction ID not found");
        return;
      }
      setPaymentTransactionId(txnId);
      setPaymentAllocationVisible(true);
      return;
    }

    // Get the ACTUAL database ID - use originalId first, then extract numeric ID from 'id' field
    // The 'id' field might be formatted like "bank-statement-123" so we need the original
    let transactionId = rowData?.originalId;

    // If originalId is not set, try to extract from the raw data
    if (!transactionId) {
      // Check if the id looks like a number
      const rawId = rowData?.id;
      if (typeof rawId === "number") {
        transactionId = rawId;
      } else if (typeof rawId === "string") {
        // Try to parse as number or extract from formatted string
        const parsed = parseInt(rawId, 10);
        if (!isNaN(parsed)) {
          transactionId = parsed;
        } else {
          // Extract number from end of string like "bank-statement-123"
          const match = rawId.match(/(\d+)$/);
          if (match) {
            transactionId = parseInt(match[1], 10);
          }
        }
      }
    }

    if (!transactionId) {
      console.error(
        "Cannot delete: Transaction ID not found in rowData:",
        rowData
      );
      message.error("Cannot delete: Transaction ID not found");
      return;
    }

    // Confirm deletion
    Modal.confirm({
      title: "Delete Transaction",
      content: `Are you sure you want to delete transaction #${
        rowData?.number || rowData?.transactionId || transactionId
      }? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          // Verify the transaction exists before deleting
          try {
            await getTransactionById(transactionId);
          } catch (verifyError) {
            // If verification fails, still try to delete - the transaction might exist but verification endpoint has issues
          }

          await deleteTransaction(transactionId);
          message.success("Transaction deleted successfully");

          // Remove from local state immediately
          setAllTransactions((prev) =>
            prev.filter((t) => {
              const tId = t.originalId || t.id;
              return String(tId) !== String(transactionId);
            })
          );

          // Invalidate cache and trigger refresh
          invalidateCacheByPrefix("bank_statements_");
          invalidateCacheByPrefix("transactions_");
        } catch (error) {
          console.error("Error deleting transaction:", error);
          console.error("Transaction ID attempted:", transactionId);
          console.error("Full rowData:", rowData);

          // Provide more helpful error message
          const errorMsg =
            error?.response?.data?.error ||
            error?.response?.data?.detail ||
            error?.message ||
            "Failed to delete transaction";

          if (errorMsg.includes("No Transaction matches")) {
            message.error(
              `Transaction #${transactionId} does not exist in the database. It may have already been deleted. Refreshing the list...`
            );
            // Remove from local state since it doesn't exist
            setAllTransactions((prev) =>
              prev.filter((t) => {
                const tId = t.originalId || t.id;
                return String(tId) !== String(transactionId);
              })
            );
            invalidateCacheByPrefix("bank_statements_");
            invalidateCacheByPrefix("transactions_");
          } else {
            message.error(errorMsg);
          }
        }
      },
    });
  }, []);

  // Handle edit cash transaction
  const handleEditCash = useCallback((rowData) => {
    const cashId = rowData?.originalId || rowData?.id;
    if (!cashId) {
      message.error("Cannot edit: Cash ID not found");
      return;
    }
    setEditingCashId(cashId);
    setCreateCashModalVisible(true);
  }, []);

  // Handle delete cash transaction
  const handleDeleteCash = useCallback(
    (rowData) => {
      const allocatedAmount = parseFloat(rowData?.allocatedAmount || 0);
      const allocationsArray = Array.isArray(rowData?.allocations)
        ? rowData.allocations
        : [];
      const allocationStatus = rowData?.allocationStatus;

      const hasAllocations =
        allocatedAmount > 0 ||
        allocationsArray.length > 0 ||
        allocationStatus === "Fully Allocated" ||
        allocationStatus === "Partially Allocated";

      if (hasAllocations) {
        // Open allocation modal to unallocate first
        const cashId = rowData?.originalId || rowData?.id;
        if (!cashId) {
          message.error("Cannot open allocations: Cash ID not found");
          return;
        }
        setCashId(cashId);
        setCashAllocationVisible(true);
        return;
      }

      // If no allocations, proceed with delete
      const cashId = rowData?.originalId || rowData?.id;
      if (!cashId) {
        message.error("Cannot delete: Cash ID not found");
        return;
      }

      Modal.confirm({
        title: "Delete Cash Payment",
        content: `Are you sure you want to delete Cash Payment #${
          rowData?.number || cashId
        }?`,
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await deleteCash(cashId);
            message.success("Cash payment deleted successfully");

            // Remove from local state immediately (preserves filters)
            setAllTransactions((prev) =>
              prev.filter((t) => {
                const tId = t.originalId || t.id;
                return String(tId) !== String(cashId);
              })
            );

            // Invalidate cache for future refresh
            invalidateCacheByPrefix("cash_");
            invalidateCacheByPrefix("transactions_");
          } catch (error) {
            console.error("Error deleting cash:", error);
            const backendMsg =
              error?.response?.data?.detail ||
              error?.response?.data?.message ||
              (typeof error?.response?.data === "string"
                ? error.response.data
                : null);

            if (backendMsg && backendMsg.toLowerCase().includes("allocation")) {
              Modal.error({
                title: "Cannot Delete Cash Payment - Has Allocations",
                content: (
                  <div>
                    <p>{backendMsg}</p>
                    <Button
                      type="primary"
                      style={{ marginTop: 16 }}
                      onClick={() => {
                        Modal.destroyAll();
                        setCashId(cashId);
                        setCashAllocationVisible(true);
                      }}
                    >
                      Go to Allocations
                    </Button>
                  </div>
                ),
              });
              return;
            }

            Modal.error({
              title: "Cannot Delete Cash Payment",
              content:
                backendMsg ||
                "An error occurred while deleting the cash payment.",
            });
          }
        },
      });
    },
    [fetchAllTransactions, clearCacheAndRefresh]
  );

  // Handle edit credit transaction
  const handleEditCredit = (rowData) => {
    const creditId = rowData?.originalId || rowData?.id;
    if (!creditId) {
      message.error("Cannot edit: Credit ID not found");
      return;
    }
    setEditingCreditId(creditId);
    setCreateCreditModalVisible(true);
  };

  // Handle delete credit transaction
  const handleDeleteCredit = useCallback(
    (rowData) => {
      const allocatedAmount = parseFloat(rowData?.allocatedAmount || 0);
      const allocationsArray = Array.isArray(rowData?.allocations)
        ? rowData.allocations
        : [];
      const allocationStatus = rowData?.allocationStatus;

      const hasAllocations =
        allocatedAmount > 0 ||
        allocationsArray.length > 0 ||
        allocationStatus === "Fully Allocated" ||
        allocationStatus === "Partially Allocated";

      if (hasAllocations) {
        // Open allocation modal to unallocate first
        const creditId = rowData?.originalId || rowData?.id;
        if (!creditId) {
          message.error("Cannot open allocations: Credit ID not found");
          return;
        }
        setCreditId(creditId);
        setCreditAllocationVisible(true);
        return;
      }

      // If no allocations, proceed with delete
      const creditId = rowData?.originalId || rowData?.id;
      if (!creditId) {
        message.error("Cannot delete: Credit ID not found");
        return;
      }

      Modal.confirm({
        title: "Delete Credit Note",
        content: `Are you sure you want to delete Credit Note #${
          rowData?.number || creditId
        }?`,
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await deleteCredit(creditId);
            message.success("Credit note deleted successfully");

            // Remove from local state immediately (preserves filters)
            setAllTransactions((prev) =>
              prev.filter((t) => {
                const tId = t.originalId || t.id;
                return String(tId) !== String(creditId);
              })
            );

            // Invalidate cache for future refresh
            invalidateCacheByPrefix("credits_");
            invalidateCacheByPrefix("transactions_");
          } catch (error) {
            console.error("Error deleting credit:", error);
            const backendMsg =
              error?.response?.data?.detail ||
              error?.response?.data?.message ||
              (typeof error?.response?.data === "string"
                ? error.response.data
                : null);

            if (backendMsg && backendMsg.toLowerCase().includes("allocation")) {
              Modal.error({
                title: "Cannot Delete Credit Note - Has Allocations",
                content: (
                  <div>
                    <p>{backendMsg}</p>
                    <Button
                      type="primary"
                      style={{ marginTop: 16 }}
                      onClick={() => {
                        Modal.destroyAll();
                        setCreditId(creditId);
                        setCreditAllocationVisible(true);
                      }}
                    >
                      Go to Allocations
                    </Button>
                  </div>
                ),
              });
              return;
            }

            Modal.error({
              title: "Cannot Delete Credit Note",
              content:
                backendMsg ||
                "An error occurred while deleting the credit note.",
            });
          }
        },
      });
    },
    [fetchAllTransactions, clearCacheAndRefresh]
  );

  // Bulk delete selected rows
  const handleBulkDelete = useCallback(() => {
    if (selectedRows.length === 0) return;
    const hasAllocated = selectedRows.some(
      (r) =>
        parseFloat(r.allocatedAmount || 0) > 0 ||
        (Array.isArray(r.allocations) && r.allocations.length > 0) ||
        r.allocationStatus === "Fully Allocated" ||
        r.allocationStatus === "Partially Allocated"
    );
    if (hasAllocated) {
      Modal.warning({
        title: "Cannot Bulk Delete",
        content:
          "Some selected transactions have allocations. Please unallocate them first, or deselect them and try again.",
      });
      return;
    }
    Modal.confirm({
      title: `Delete ${selectedRows.length} Transaction(s)?`,
      content: `This will permanently delete ${selectedRows.length} selected transaction(s). This action cannot be undone.`,
      okText: "Delete All",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        let successCount = 0;
        let failCount = 0;
        for (const row of selectedRows) {
          try {
            const id = row.originalId || row.id;
            const type = row.type || row.transactionType;
            if (type === "Bank Statement" || type === "bankStatement") {
              await deleteTransaction(id);
            } else if (type === "Overpayment" || type === "overpayment") {
              await deleteOverpayment(id);
            } else if (type === "Cash" || type === "cash") {
              await deleteCash(id);
            } else if (type === "Credit" || type === "credit") {
              await deleteCredit(id);
            } else {
              await deleteTransaction(id);
            }
            successCount++;
          } catch (e) {
            failCount++;
          }
        }
        if (successCount > 0) {
          message.success(`Deleted ${successCount} transaction(s)`);
          const deletedIds = new Set(selectedRows.map((r) => r.originalId || r.id));
          setAllTransactions((prev) =>
            prev.filter((t) => !deletedIds.has(t.originalId || t.id))
          );
          setSelectedRows([]);
        }
        if (failCount > 0) {
          message.error(`Failed to delete ${failCount} transaction(s)`);
        }
      },
    });
  }, [selectedRows]);

  // Handle modal closures and refresh data - OPTIMIZED to only update the specific row
  const handleAllocationSuccess = useCallback(
    async (data) => {
      // Don't close modals - let user close them manually
      // OPTIMIZED: Only fetch and update the specific item that changed, not all 6000+ transactions

      try {
        let updatedItem = null;
        let itemId = null;
        let itemType = null; // The display type used in the grid (e.g., "Bank Statement", "Credit")

        if (data?.type === "transaction" && data?.transactionId) {
          // Fetch just the one transaction that was updated
          const response = await getTransactionById(data.transactionId);
          updatedItem = response.data || response;
          itemId = data.transactionId;
          itemType = "Bank Statement"; // Grid uses "Bank Statement" not "transaction"
        } else if (data?.type === "credit" && data?.creditId) {
          // Fetch just the one credit that was updated
          const response = await getCreditById(data.creditId, true);
          updatedItem = response.data || response;
          itemId = data.creditId;
          itemType = "Credit";
        } else if (data?.type === "cash" && data?.cashId) {
          // Fetch just the one cash that was updated
          const response = await getCashById(data.cashId, true);
          updatedItem = response.data || response;
          itemId = data.cashId;
          itemType = "Cash";
        } else if (data?.type === "overpayment" && data?.overpaymentId) {
          // Fetch just the one overpayment that was updated
          const response = await getOverpaymentDetails(data.overpaymentId);
          updatedItem = response.data || response;
          itemId = data.overpaymentId;
          itemType = "Overpayment";
        }

        if (updatedItem && itemId && itemType) {
          // Update just that specific row in the transactions state
          setAllTransactions((prevTransactions) =>
            prevTransactions.map((txn) => {
              // Match by type AND originalId (or id) - use String comparison for safety
              const txnId = String(txn.originalId || txn.id);
              const targetId = String(itemId);
              const typeMatches = txn.type === itemType;

              if (typeMatches && txnId === targetId) {
                // Re-calculate allocation status from updated data
                const amount = parseFloat(
                  updatedItem.amount ||
                    updatedItem.total_amount ||
                    txn.amount ||
                    0
                );

                // Calculate allocated amount - for overpayments, backend returns remaining_amount
                // so we need to calculate: allocated = amount - remaining
                let allocatedAmount = parseFloat(
                  updatedItem.allocated_amount || updatedItem.allocated_sum || 0
                );

                // Special handling for overpayments which return remaining_amount instead of allocated_amount
                if (
                  itemType === "Overpayment" &&
                  updatedItem.remaining_amount !== undefined
                ) {
                  const remainingFromBackend = parseFloat(
                    updatedItem.remaining_amount || 0
                  );
                  // allocated = total - remaining (use absolute values for calculation)
                  allocatedAmount =
                    Math.abs(amount) - Math.abs(remainingFromBackend);
                }

                const direction = amount >= 0 ? 1 : -1;
                let remainingAmount = amount - allocatedAmount * direction;
                if (
                  (amount < 0 && remainingAmount > 0) ||
                  (amount >= 0 && remainingAmount < 0)
                ) {
                  remainingAmount = 0;
                }

                const percentAllocated =
                  amount !== 0 ? (allocatedAmount / Math.abs(amount)) * 100 : 0;

                // Use backend allocation_status if available (includes Overpay, etc.)
                let allocationStatus = "Unallocated";
                if (updatedItem.allocation_status?.label) {
                  allocationStatus = updatedItem.allocation_status.label;
                } else if (updatedItem.allocation_status?.type) {
                  const typeMap = {
                    overpay: "Overpay",
                    fully_allocated: "Fully Allocated",
                    partially_allocated: "Partially Allocated",
                    unallocated: "Unallocated",
                  };
                  allocationStatus =
                    typeMap[updatedItem.allocation_status.type] ||
                    updatedItem.allocation_status.type;
                } else if (percentAllocated >= 99.99) {
                  allocationStatus = "Fully Allocated";
                } else if (percentAllocated > 0) {
                  allocationStatus = "Partially Allocated";
                }

                // Get updated allocations array
                const rawAllocations =
                  updatedItem.allocations ||
                  updatedItem.payment_allocations ||
                  updatedItem.cash_allocations ||
                  updatedItem.credit_allocations ||
                  updatedItem.overpayment_allocations ||
                  txn.allocations ||
                  [];
                const allocationsArray = (
                  Array.isArray(rawAllocations) ? rawAllocations : []
                ).filter((a) => a !== null);

                // Return updated row with recalculated status
                // IMPORTANT: Keep ALL original formatted fields (number, date, bank, etc.)
                // Update partner info from updatedItem AND allocation-related data
                
                // Extract partner info from updatedItem directly
                // Backend returns: partner (ID as number), vendor_name (name as string)
                let updatedPartnerName =
                  updatedItem.partner?.name ||
                  updatedItem.partner_name ||
                  updatedItem.vendor_name ||
                  (typeof updatedItem.partner === "string" ? updatedItem.partner : null);
                let updatedPartnerId =
                  updatedItem.partner?.id ||
                  updatedItem.partner_id ||
                  updatedItem.vendor_id ||
                  (typeof updatedItem.partner === "number" ? updatedItem.partner : null);

                // If partner info not found directly, try to get from allocations
                if (allocationsArray.length > 0 && (!updatedPartnerName || !updatedPartnerId)) {
                  const firstAlloc = allocationsArray[0];
                  if (!updatedPartnerName) {
                    updatedPartnerName =
                      firstAlloc?.partner_name ||
                      firstAlloc?.invoice?.partner_name ||
                      firstAlloc?.invoice?.partner?.name ||
                      null;
                  }
                  if (!updatedPartnerId) {
                    updatedPartnerId =
                      firstAlloc?.partner_id ||
                      firstAlloc?.partner ||
                      firstAlloc?.invoice?.partner_id ||
                      firstAlloc?.invoice?.partner?.id ||
                      (typeof firstAlloc?.invoice?.partner === "number" ? firstAlloc?.invoice?.partner : null) ||
                      null;
                  }
                }

                // Determine the partner to display:
                // - If transaction has no allocations (allocatedAmount is 0), clear the partner
                // - If transaction has allocations, use partner from backend response
                let newPartner = txn.partner;
                let newPartnerId = txn.partnerId;
                
                // Check if truly unallocated - use numeric check, not string comparison
                const hasNoAllocations = allocatedAmount === 0 || allocationsArray.length === 0;
                
                if (hasNoAllocations) {
                  // No allocations - clear partner
                  newPartner = "-";
                  newPartnerId = null;
                } else if (updatedPartnerName || updatedPartnerId) {
                  // Has allocations and backend returned partner info
                  newPartner = updatedPartnerName || txn.partner;
                  newPartnerId = updatedPartnerId ? Number(updatedPartnerId) : txn.partnerId;
                }

                return {
                  ...txn,
                  // Update partner info based on allocation status
                  partner: newPartner,
                  partnerId: newPartnerId,
                  allocatedAmount,
                  remainingAmount,
                  percentAllocated,
                  allocationStatus,
                  allocations: allocationsArray,
                  invoiceCount: allocationsArray.filter(
                    (a) => a && (a.invoice_id || a.invoice)
                  ).length,
                };
              }
              return txn;
            })
          );

          // Invalidate cache so next full refresh picks up the changes
          // But don't trigger a refresh - we already updated the state
          if (cacheKey) {
            invalidateCacheByPrefix("transactions_");
          }

          // Force refresh statistics to reflect updated allocations
          fetchStatistics(true);
        } else {
          // Fallback: if no data provided, do full refresh (backward compatibility)
          clearCacheAndRefresh();
          fetchStatistics(true);
        }
      } catch (error) {
        console.error("Error updating single item after allocation:", error);
        // On error, fall back to full refresh
        clearCacheAndRefresh();
        fetchStatistics(true);
      }
    },
    [clearCacheAndRefresh, fetchStatistics, cacheKey]
  );

  const handleAllocationCancel = useCallback(() => {
    setPaymentAllocationVisible(false);
    setCreditAllocationVisible(false);
    setCashAllocationVisible(false);
    setOverpaymentAllocationVisible(false);
    setEditStatementVisible(false);
    // DON'T refresh on cancel - only refresh on actual allocation success
    // This prevents unnecessary API calls when user just closes the modal
  }, []);

  // Handle view invoice
  const handleViewInvoice = useCallback((invoiceId) => {
    if (invoiceId === null || invoiceId === undefined) {
      console.error("No invoice ID provided to handleViewInvoice");
      message.error("No invoice ID available");
      return;
    }

    const parsedId =
      typeof invoiceId === "string" ? parseInt(invoiceId, 10) : invoiceId;

    if (!parsedId || Number.isNaN(parsedId)) {
      message.error("Invalid invoice ID");
      return;
    }
    setViewingInvoiceId(parsedId);
    setViewInvoiceModalVisible(true);
  }, []);

  // AG Grid context to pass handlers to cell renderers
  const gridContext = useMemo(
    () => ({
      onAffectPayment: handleAffectPayment,
      onAffectOverpayment: handleAffectOverpayment,
      onAffectCredit: handleAffectCredit,
      onAffectCash: handleAffectCash,
      onEditStatement: handleEditStatement,
      onEditTransaction: handleEditTransaction,
      onDeleteTransaction: handleDeleteTransaction,
      onDeleteOverpayment: handleDeleteOverpayment,
      onEditCash: handleEditCash,
      onDeleteCash: handleDeleteCash,
      onEditCredit: handleEditCredit,
      onDeleteCredit: handleDeleteCredit,
      onViewInvoice: handleViewInvoice,
      onViewPartner: (partnerId) => {
        if (!partnerId) return;
        // Switch to Partner Transactions tab and pass the selected partner
        setActiveTabKey("partner-transactions");
        setSelectedPartnerForTransactions(partnerId);
      },
    }),
    [
      handleAffectPayment,
      handleAffectOverpayment,
      handleAffectCredit,
      handleAffectCash,
      handleEditStatement,
      handleEditTransaction,
      handleDeleteTransaction,
      handleDeleteOverpayment,
      handleEditCash,
      handleDeleteCash,
      handleEditCredit,
      handleDeleteCredit,
      handleViewInvoice,
    ]
  );

  // AG Grid Column Definitions - prepend checkbox selection column
  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        width: 50,
        maxWidth: 50,
        pinned: "left",
        lockPosition: true,
        suppressMenu: true,
        sortable: false,
        filter: false,
        resizable: false,
      },
      ...getUnifiedTransactionsColumns(gridContext),
    ],
    [gridContext]
  );

  // Default Column Definition for AG Grid
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      // Enable AND/OR conditions for text filters
      filterParams: {
        suppressAndOrCondition: false,
        buttons: ["apply", "reset"],
      },
    }),
    []
  );

  // AG Grid Options - memoized to prevent unnecessary re-renders
  const gridOptions = useMemo(
    () => ({
      rowSelection: "multiple",
      suppressRowClickSelection: true,
      enableBrowserTooltips: true,
      enableRangeSelection: true,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      pagination: true,
      paginationPageSize: pageSize,
      paginationPageSizeSelector: [25, 50, 100, 200],
      paginationPosition: "top",
      onPaginationChanged: (params) => {
        // CRITICAL: This callback is ONLY for tracking pagination state
        // It does NOT trigger any data fetching - pagination is 100% client-side
        // The grid already has all the data in rowData={filteredTransactions}
        // NO API calls, NO refetches, NO cache clearing - just state tracking
        if (params.newPage !== undefined) {
          const newPage = params.api.paginationGetCurrentPage() + 1; // AG Grid is 0-indexed
          const newPageSize = params.api.paginationGetPageSize();
          // Silently update state - no side effects, no refetches
          handlePaginationChange(newPage, newPageSize);
        }
      },
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
          },
          {
            id: "filters",
            labelDefault: "Filters",
            labelKey: "filters",
            iconKey: "filter",
            toolPanel: "agFiltersToolPanel",
          },
        ],
      },
      domLayout: "normal",
      // Performance optimizations
      suppressPaginationPanel: false,
      suppressScrollOnNewData: true,
      animateRows: false, // Disable row animations for better performance
      rowBuffer: 30, // Increased from 10 to prevent white areas when scrolling fast
      suppressCellFocus: false,
      debounceVerticalScrollbar: true, // Improve scroll performance
      cacheBlockSize: 100, // Cache more rows for smoother scrolling
      // NOTE: Column state, filter state, and sort state are automatically persisted
      // by AgGridWithColumnState component via the useColumnState hook.
      // DO NOT add onGridReady, onFilterChanged, onSortChanged, onColumnMoved,
      // onColumnResized, or onColumnVisible here as they will conflict with
      // the automatic state management provided by AgGridWithColumnState.
    }),
    [pageSize, handlePaginationChange] // Removed currentPage from dependencies to prevent re-renders
  );

  // Submit single transaction edit
  const submitEditTransaction = async () => {
    try {
      const values = await editTxnForm.validateFields();
      setEditTxnLoading(true);
      // If header changed OR bank changed in form, persist statement updates first
      if (editTxnStatementId) {
        try {
          const stmt = await getStatementById(editTxnStatementId);
          const currentBankId =
            (stmt?.bank &&
              (typeof stmt.bank === "object" ? stmt.bank.id : stmt.bank)) ||
            null;

          // Check if bank changed in header
          const bankChanged =
            editHeaderDraft.bankId && editHeaderDraft.bankId !== currentBankId;

          const headerChanged =
            bankChanged ||
            Number(editHeaderDraft.opening_balance ?? 0) !==
              Number(stmt?.opening_balance ?? 0) ||
            Number(editHeaderDraft.closing_balance ?? 0) !==
              Number(stmt?.closing_balance ?? 0);

          if (headerChanged) {
            // Keep transactions intact when updating statement header
            const transactions =
              stmt?.transactions?.map((t) => ({
                id: t.id,
                transaction_date: t.transaction_date,
                description: t.description,
                detail: t.detail,
                type: t.type,
                amount: t.amount,
                reference: t.reference,
              })) || [];

            // Use bank from header draft, otherwise keep current
            const newBankId = editHeaderDraft.bankId || currentBankId;

            // Get brand ID from statement (required field)
            const brandId =
              (stmt?.brand &&
                (typeof stmt.brand === "object"
                  ? stmt.brand.id
                  : stmt.brand)) ||
              stmt?.brand_id ||
              null;

            const stmtPayload = {
              bank: newBankId,
              brand: brandId,
              statement_date: stmt.statement_date,
              opening_balance:
                editHeaderDraft.opening_balance ?? stmt.opening_balance ?? 0,
              closing_balance:
                editHeaderDraft.closing_balance ?? stmt.closing_balance ?? 0,
              start_date: stmt.start_date,
              end_date: stmt.end_date,
              notes: stmt.notes || "",
              transactions,
            };
            await updateStatement(editTxnStatementId, stmtPayload);
          }
        } catch (stmtErr) {
          // Log statement update error but don't fail the transaction update
          // This can happen when bank changes - statement update might fail but transaction update will succeed
          // Silently handle these errors - they're non-critical since the transaction update will proceed
          const stmtErrorMsg =
            stmtErr?.response?.data?.error ||
            stmtErr?.response?.data?.detail ||
            stmtErr?.message;

          // Don't show error toasts for statement update failures when bank changes
          // The transaction update will still succeed
          console.warn(
            "Statement update error (non-critical, transaction will still update):",
            stmtErrorMsg || stmtErr
          );
        }
      }

      const payload = {
        transaction_date: values.transaction_date
          ? values.transaction_date.format("YYYY-MM-DD")
          : null,
        description: values.description || "",
        detail: values.detail || "DEBIT",
        type: values.type || "ACH_DEBIT",
        amount: values.amount,
        reference: values.reference || "",
        // Backend requires the statement field on update
        // Use the statement ID from the header draft if bank was changed, otherwise use original
        statement: editTxnStatementId,
      };

      // Ensure transaction exists before updating
      if (!editTxnId) {
        throw new Error("Transaction ID is required");
      }

      await updateTransaction(editTxnId, payload);
      message.success("Transaction updated");
      setEditTxnVisible(false);
      setEditTxnId(null);

      // Refresh data in background - don't show errors if refresh fails
      // (transaction was already saved successfully)
      clearCacheAndRefresh(true); // suppressErrors = true
    } catch (err) {
      if (err?.errorFields) return; // form validation error already shown

      // Provide more specific error messages
      let errorMessage = "Failed to update transaction";
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      // Check if it's the "No Transaction matches" error
      if (
        errorMessage.includes("No Transaction matches") ||
        errorMessage.includes("not found")
      ) {
        errorMessage =
          "Transaction not found. Please refresh the page and try again.";
      }

      console.error("Transaction update error:", err);
      message.error(errorMessage);
    } finally {
      setEditTxnLoading(false);
    }
  };

  // Show message if no brands are selected
  if (!selectedBrands || selectedBrands.length === 0) {
    return (
      <div
        style={{ padding: "0", margin: "0", width: "100%", minHeight: "100vh" }}
      >
        <div style={{ padding: "16px", margin: "0" }}>
          <div style={{ marginBottom: "16px" }}>
            <h2
              style={{
                margin: "0 0 16px 0",
                fontSize: "24px",
                fontWeight: "600",
              }}
            >
              Transactions & Allocations
            </h2>
          </div>
          <Card>
            <Alert
              message="No Brands Selected"
              description="Please select at least one brand from the global filters to view transactions and allocations."
              type="info"
              showIcon
            />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ padding: "0", margin: "0", width: "100%", minHeight: "100vh" }}
    >
      <div style={{ padding: "16px", margin: "0" }}>
        <div style={{ marginBottom: "16px" }}>
          <h2
            style={{
              margin: "0 0 16px 0",
              fontSize: "24px",
              fontWeight: "600",
            }}
          >
            Transactions & Allocations
          </h2>
        </div>

        {/* Allocation Statistics Widgets */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button
                onClick={() => {
                  const today = dayjs();
                  setDateFilter([today.startOf("month"), today.endOf("month")]);
                }}
              >
                This Month
              </Button>
              <Button
                onClick={() => {
                  const lastMonth = dayjs().subtract(1, "month");
                  setDateFilter([
                    lastMonth.startOf("month"),
                    lastMonth.endOf("month"),
                  ]);
                }}
              >
                Last Month
              </Button>
              <Button
                onClick={() => {
                  const today = dayjs();
                  setDateFilter([today.startOf("year"), today.endOf("year")]);
                }}
              >
                Year to Date
              </Button>
              <RangePicker
                value={dateFilter}
                onChange={setDateFilter}
                format="YYYY-MM-DD"
              />
              {dateFilter && (
                <Button onClick={() => setDateFilter(null)}>Clear</Button>
              )}
            </Space>
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "nowrap", overflowX: "auto" }}>
            <Statistic
              title="Total Allocations"
              value={
                isConverted
                  ? statistics.totalAllocations * exchangeRate
                  : statistics.totalAllocations
              }
              prefix={<DollarOutlined />}
              suffix={displayCurrency}
              valueStyle={{ color: "#1890ff", whiteSpace: "nowrap" }}
              precision={decimalPrecision}
            />
            <Statistic
              title="Total Unallocated"
              value={
                isConverted
                  ? totalUnallocated * exchangeRate
                  : totalUnallocated
              }
              prefix={<WarningOutlined />}
              suffix={displayCurrency}
              valueStyle={{ color: "#ff4d4f", whiteSpace: "nowrap" }}
              precision={decimalPrecision}
            />
            <Statistic
              title="Pending Allocations"
              value={statistics.pendingAllocations}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#faad14", whiteSpace: "nowrap" }}
            />
            <Statistic
              title="Fully Allocated"
              value={statistics.fullyAllocated}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a", whiteSpace: "nowrap" }}
            />
            <Statistic
              title="Total Transactions"
              //value={statistics.totalTransactions}
              value={
                totalTransactionCount > 0 ? totalTransactionCount : statistics.totalTransactions
              }
              valueStyle={{ color: "#595959", whiteSpace: "nowrap" }}
            />
          </div>
        </Card>

        {/* Tabs - preserve inactive tabs to maintain grid state */}
        <Tabs 
          activeKey={activeTabKey} 
          onChange={setActiveTabKey} 
          type="card"
          destroyInactiveTabPane={false}
        >
          <TabPane tab="Transactions" key="transactions">
            {/* Type Filters */}
            <Card style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <Space wrap>
                  <Checkbox
                    checked={selectedTypes.bankStatement}
                    onChange={(e) =>
                      handleTypeFilterChange("bankStatement", e.target.checked)
                    }
                  >
                    <span style={{ color: "#000" }}>Bank Statement</span>
                  </Checkbox>
                  <Checkbox
                    checked={selectedTypes.overpayment}
                    onChange={(e) =>
                      handleTypeFilterChange("overpayment", e.target.checked)
                    }
                  >
                    <span style={{ color: "#000" }}>Overpayment</span>
                  </Checkbox>
                  <Checkbox
                    checked={selectedTypes.cash}
                    onChange={(e) =>
                      handleTypeFilterChange("cash", e.target.checked)
                    }
                  >
                    <span style={{ color: "#000" }}>Cash</span>
                  </Checkbox>
                  <Checkbox
                    checked={selectedTypes.credit}
                    onChange={(e) =>
                      handleTypeFilterChange("credit", e.target.checked)
                    }
                  >
                    <span style={{ color: "#000" }}>Credit</span>
                  </Checkbox>
                </Space>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">
                  Showing {allTransactions.length} of {totalTransactionCount}{" "}
                  transactions
                  {isLoadingMore && (
                    <span style={{ marginLeft: 8, color: "#1890ff" }}>
                      <Spin size="small" style={{ marginRight: 4 }} />
                      Loading more...
                    </span>
                  )}
                </Text>
                <Space>
                  <Button
                    type="default"
                    icon={<FileExcelOutlined />}
                    onClick={() => {
                      try {
                        // Export filtered data from AG Grid - respects all column filters
                        const dataToExport = [];

                        // gridRef.current IS the API directly (set by AgGridWithColumnState)
                        const gridApi = gridRef.current;
                        if (
                          gridApi &&
                          typeof gridApi.forEachNodeAfterFilterAndSort ===
                            "function"
                        ) {
                          gridApi.forEachNodeAfterFilterAndSort((node) => {
                            if (node.data) {
                              dataToExport.push({
                                "Transaction ID":
                                  node.data.number || node.data.id || "",
                                Type:
                                  node.data.transactionType ||
                                  node.data.type ||
                                  "",
                                Date:
                                  node.data.date ||
                                  node.data.formattedDate ||
                                  node.data.transaction_date ||
                                  "",
                                Partner:
                                  node.data.partner ||
                                  node.data.partnerName ||
                                  "",
                                Bank:
                                  node.data.bank || node.data.bankName || "",
                                Description: node.data.description || "",
                                Detail: node.data.detail || "",
                                Reference: node.data.reference || "",
                                Amount: parseFloat(
                                  node.data.amount || 0
                                ).toFixed(decimalPrecision),
                                Allocated: parseFloat(
                                  node.data.allocatedAmount || 0
                                ).toFixed(decimalPrecision),
                                Remaining: parseFloat(
                                  node.data.remainingAmount || 0
                                ).toFixed(decimalPrecision),
                                Status: node.data.allocationStatus || "",
                                "Invoice Number":
                                  node.data.invoice_number ||
                                  (node.data.allocations || [])
                                    .map(
                                      (a) =>
                                        a?.invoice_number ||
                                        a?.invoice?.number ||
                                        ""
                                    )
                                    .filter(Boolean)
                                    .join(", ") ||
                                  "",
                              });
                            }
                          });
                        }

                        if (dataToExport.length === 0) {
                          message.warning(
                            "No transactions to export (check your filters)"
                          );
                          return;
                        }

                        const timestamp = dayjs().format("YYYYMMDD_HHmmss");
                        secureExportToExcel(
                          dataToExport,
                          `transactions_export_${timestamp}.xlsx`
                        );
                        message.success(
                          `Exported ${dataToExport.length} transactions successfully!`
                        );
                      } catch (error) {
                        console.error("Export error:", error);
                        message.error("Failed to export transactions");
                      }
                    }}
                    disabled={loading || allTransactions.length === 0}
                  >
                    Export
                  </Button>
                  <Button
                    type="default"
                    icon={<UploadOutlined />}
                    onClick={() => setUploadStatementVisible(true)}
                  >
                    Upload Bank Statement
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setStatementId(null);
                      setCreateStatementVisible(true);
                    }}
                  >
                    Create Bank Statement
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={showCreateCashModal}
                  >
                    Create Cash Payment
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={showCreateCreditModal}
                  >
                    Create Credit
                  </Button>
                </Space>
              </div>
              {selectedRows.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleBulkDelete}
                  >
                    Delete Selected ({selectedRows.length})
                  </Button>
                </div>
              )}
            </Card>

            {/* Unified AG Grid Table */}
            <Card>
              <ResizableGridContainer defaultHeight={700} minHeight={300} maxHeight={1400}>
                <AgGridWithColumnState
                  key={`transactions-allocations-grid-${currencyRefreshKey}`}
                  tableId="transactions-allocations-unified"
                  gridApiRef={gridRef}
                  columnDefs={columnDefs}
                  rowData={filteredTransactions}
                  defaultColDef={defaultColDef}
                  context={gridContext}
                  {...gridOptions}
                  onSelectionChanged={onSelectionChanged}
                  loading={loading}
                />
              </ResizableGridContainer>
            </Card>
          </TabPane>

          <TabPane tab="Statement History" key="statement-history">
            <Card>
              <StatementHistory
                selectedBrand={
                  selectedBrands.length > 0 ? selectedBrands[0] : null
                }
                ref={statementHistoryRef}
              />
            </Card>
          </TabPane>

          <TabPane tab="Transactions History" key="transactions-history">
            <Card>
              <Suspense fallback={<Spin />}>
                <TransactionsHistory
                  selectedBrand={
                    selectedBrands.length > 0 ? selectedBrands[0] : null
                  }
                />
              </Suspense>
            </Card>
          </TabPane>

          <TabPane tab="Allocation History" key="allocation-history">
            <Card>
              <Suspense fallback={<Spin />}>
                <AllocationHistory
                  selectedBrand={
                    selectedBrands.length > 0 ? selectedBrands[0] : null
                  }
                />
              </Suspense>
            </Card>
          </TabPane>

          <TabPane tab="Partner Transactions" key="partner-transactions">
            <Card>
              <Suspense fallback={<Spin />}>
                <PartnerTransactions
                  selectedBrand={
                    selectedBrands.length > 0 ? selectedBrands[0] : null
                  }
                  partnerId={selectedPartnerForTransactions}
                />
              </Suspense>
            </Card>
          </TabPane>
        </Tabs>
      </div>

      {/* Create/Edit Cash Payment Modal - only mount when needed */}
      {createCashModalVisible && (
        <CreateCashModal
          visible={createCashModalVisible}
          onCancel={handleCreateCashCancel}
          onSuccess={handleCreateCashSuccess}
          selectedBrand={selectedBrands.length > 0 ? selectedBrands[0] : null}
          cashId={editingCashId}
        />
      )}

      {/* Create/Edit Credit Note Modal - only mount when needed */}
      {createCreditModalVisible && (
        <CreateCreditModal
          visible={createCreditModalVisible}
          onCancel={handleCreateCreditCancel}
          onSuccess={handleCreateCreditSuccess}
          selectedBrand={selectedBrands.length > 0 ? selectedBrands[0] : null}
          creditId={editingCreditId}
          defaultCreditType="bill"
        />
      )}

      {/* Payment Allocation Modal */}
      <Modal
        title="Payment Allocation"
        open={paymentAllocationVisible}
        onCancel={handleAllocationCancel}
        footer={null}
        width="99vw"
        destroyOnClose
        centered
        style={{ top: 10, maxWidth: "none" }}
        zIndex={1050}
        bodyStyle={{ maxHeight: "85vh", overflow: "auto", padding: "12px" }}
        wrapClassName="payment-allocation-modal"
      >
        <PaymentAllocation
          transactionId={paymentTransactionId}
          selectedBrands={selectedBrands}
          onSuccess={handleAllocationSuccess}
          onCancel={handleAllocationCancel}
          onViewPartner={(partnerId) => {
            if (!partnerId) return;
            // Close the modal first, then switch to partner transactions tab
            setPaymentAllocationVisible(false);
            setActiveTabKey("partner-transactions");
            setSelectedPartnerForTransactions(partnerId);
          }}
        />
      </Modal>

      {/* Credit Allocation Modal */}
      <Modal
        title="Credit Allocation"
        open={creditAllocationVisible}
        onCancel={handleAllocationCancel}
        footer={null}
        width="99vw"
        destroyOnClose
        centered
        style={{ top: 10, maxWidth: "none" }}
        zIndex={1050}
        bodyStyle={{ maxHeight: "85vh", overflow: "auto", padding: "12px" }}
        wrapClassName="credit-allocation-modal"
      >
        <CreditAllocation
          creditId={creditId}
          selectedBrands={selectedBrands}
          onSuccess={handleAllocationSuccess}
          onCancel={handleAllocationCancel}
          onViewPartner={(partnerId) => {
            if (!partnerId) return;
            // Close the modal first, then switch to partner transactions tab
            setCreditAllocationVisible(false);
            setActiveTabKey("partner-transactions");
            setSelectedPartnerForTransactions(partnerId);
          }}
        />
      </Modal>

      {/* Cash Allocation Modal */}
      <Modal
        title="Cash Allocation"
        open={cashAllocationVisible}
        onCancel={handleAllocationCancel}
        footer={null}
        width="99vw"
        destroyOnClose
        centered
        style={{ top: 10, maxWidth: "none" }}
        zIndex={1050}
        bodyStyle={{ maxHeight: "85vh", overflow: "auto", padding: "12px" }}
        wrapClassName="cash-allocation-modal"
      >
        <CashAllocation
          cashId={cashId}
          selectedBrands={selectedBrands}
          onSuccess={handleAllocationSuccess}
          onCancel={handleAllocationCancel}
          onViewPartner={(partnerId) => {
            if (!partnerId) return;
            // Close the modal first, then switch to partner transactions tab
            setCashAllocationVisible(false);
            setActiveTabKey("partner-transactions");
            setSelectedPartnerForTransactions(partnerId);
          }}
        />
      </Modal>

      {/* OverPaymentPage Modal - for creating overpayment from transaction */}
      <Modal
        title="Create Overpayment"
        open={overPaymentPageVisible}
        onCancel={handleOverPaymentPageCancel}
        footer={null}
        width={1200}
        destroyOnClose
        centered
        style={{ top: 20 }}
        zIndex={1050}
        bodyStyle={{ maxHeight: "80vh", overflow: "auto" }}
        wrapClassName="overpayment-page-modal"
      >
        <OverPaymentPage
          transactionId={overPaymentTransactionId}
          selectedBrands={selectedBrands}
          onSuccess={handleOverPaymentPageSuccess}
          onCancel={handleOverPaymentPageCancel}
        />
      </Modal>

      {/* Overpayment Allocation Modal */}
      <Modal
        title="Overpayment Allocation"
        open={overpaymentAllocationVisible}
        onCancel={handleAllocationCancel}
        footer={null}
        width="99vw"
        destroyOnClose
        centered
        style={{ top: 10, maxWidth: "none" }}
        zIndex={1050}
        bodyStyle={{ maxHeight: "85vh", overflow: "auto", padding: "12px" }}
        wrapClassName="overpayment-allocation-modal"
      >
        <OverPaymentAllocation
          overpaymentId={overpaymentId}
          selectedBrands={selectedBrands}
          onSuccess={handleAllocationSuccess}
          onCancel={handleAllocationCancel}
          onViewPartner={(partnerId) => {
            if (!partnerId) return;
            // Close the modal first, then switch to partner transactions tab
            setOverpaymentAllocationVisible(false);
            setActiveTabKey("partner-transactions");
            setSelectedPartnerForTransactions(partnerId);
          }}
        />
      </Modal>

      {/* Upload Bank Statement with Column Mapping - only mount when needed */}
      {uploadStatementVisible && (
        <UploadBankStatementWithMapping
          visible={uploadStatementVisible}
          onCancel={() => setUploadStatementVisible(false)}
          onSuccess={() => {
            setUploadStatementVisible(false);
            // Reload page to fetch fresh data including newly uploaded statement
            window.location.reload();
          }}
          selectedBrand={selectedBrands.length > 0 ? selectedBrands[0] : null}
        />
      )}

      {/* Create Statement Modal (for creating new statements) */}
      <Modal
        title="Create Bank Statement"
        open={createStatementVisible}
        onCancel={() => {
          setCreateStatementVisible(false);
          setStatementId(null);
        }}
        footer={null}
        width={1200}
        destroyOnClose
        centered
        style={{ top: 20 }}
        zIndex={1050}
        bodyStyle={{ maxHeight: "80vh", overflow: "auto" }}
        wrapClassName="statement-modal"
      >
        <CreateStatement
          statementId={null}
          selectedBrand={selectedBrands.length > 0 ? selectedBrands[0] : null}
          onSuccess={() => {
            setCreateStatementVisible(false);
            fetchAllTransactions();
            fetchStatementHistory();
            fetchStatistics();
          }}
          onCancel={() => {
            setCreateStatementVisible(false);
          }}
        />
      </Modal>

      {/* Edit Statement Modal (for editing existing statements) */}
      <Modal
        title="Edit Statement"
        open={editStatementVisible}
        onCancel={() => {
          setEditStatementVisible(false);
          setStatementId(null);
        }}
        footer={null}
        width={1200}
        destroyOnClose
        centered
        style={{ top: 20 }}
        zIndex={1050}
        bodyStyle={{ maxHeight: "80vh", overflow: "auto" }}
        wrapClassName="statement-modal"
      >
        <CreateStatement
          statementId={statementId}
          selectedBrand={selectedBrands.length > 0 ? selectedBrands[0] : null}
          onSuccess={() => {
            setEditStatementVisible(false);
            setStatementId(null);
            fetchAllTransactions();
            fetchStatementHistory();
            fetchStatistics();
          }}
          onCancel={() => {
            setEditStatementVisible(false);
            setStatementId(null);
          }}
        />
      </Modal>

      {/* Single Transaction Edit Modal */}
      <Modal
        title="Edit Transaction"
        open={editTxnVisible}
        onCancel={() => setEditTxnVisible(false)}
        footer={null}
        width={1200}
        destroyOnClose
        centered
        style={{ top: 20 }}
        zIndex={1050}
        bodyStyle={{ maxHeight: "80vh", overflow: "auto" }}
        wrapClassName="statement-modal"
      >
        <div style={{ padding: 8 }}>
          <Space
            style={{
              width: "100%",
              justifyContent: "flex-end",
              marginBottom: 12,
            }}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={editTxnLoading}
              onClick={submitEditTransaction}
            >
              Save Transaction
            </Button>
          </Space>
          {/* Statement header mirroring Create Statement (editable) */}
          <Card size="small" style={{ marginBottom: 12 }}>
            <Row gutter={16}>
              <Col span={10}>
                <Form layout="vertical">
                  <Form.Item label="Bank Account">
                    <Select
                      showSearch
                      placeholder="Select a bank account"
                      value={editHeaderDraft.bankId || undefined}
                      onChange={(val) =>
                        setEditHeaderDraft((s) => ({ ...s, bankId: val }))
                      }
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={(banksCache || []).map((b) => ({
                        label: b.account_number
                          ? `${b.name} • ${String(b.account_number)
                              .slice(-4)
                              .padStart(String(b.account_number).length, "*")}`
                          : b.name,
                        value: b.id,
                      }))}
                    />
                  </Form.Item>
                </Form>
              </Col>
              <Col span={7}>
                <Form layout="vertical">
                  <Form.Item label="Opening Balance">
                    <InputNumber
                      style={{ width: "100%" }}
                      value={editHeaderDraft.opening_balance ?? 0}
                      onChange={(val) =>
                        setEditHeaderDraft((s) => ({
                          ...s,
                          opening_balance: Number(val || 0),
                        }))
                      }
                      step={0.01}
                    />
                  </Form.Item>
                </Form>
              </Col>
              <Col span={7}>
                <Form layout="vertical">
                  <Form.Item label="Closing Balance">
                    <InputNumber
                      style={{ width: "100%" }}
                      value={editHeaderDraft.closing_balance ?? 0}
                      onChange={(val) =>
                        setEditHeaderDraft((s) => ({
                          ...s,
                          closing_balance: Number(val || 0),
                        }))
                      }
                      step={0.01}
                    />
                  </Form.Item>
                </Form>
              </Col>
            </Row>
          </Card>
          {editTxnMeta && (
            <Row style={{ marginBottom: 8 }}>
              <Col span={12} style={{ textAlign: "right" }}>
                <Tag
                  color={
                    editTxnMeta.duplicate_status === "NEW_ENTRY"
                      ? "green"
                      : editTxnMeta.duplicate_status === "DUPLICATE"
                      ? "red"
                      : "orange"
                  }
                >
                  {editTxnMeta.duplicate_status || "STATUS"}
                </Tag>
              </Col>
            </Row>
          )}
          <Form form={editTxnForm} layout="vertical">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Date"
                  name="transaction_date"
                  rules={[{ required: true, message: "Date is required" }]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Detail"
                  name="detail"
                  rules={[{ required: true }]}
                >
                  <Select>
                    <Select.Option value="DEBIT">DEBIT</Select.Option>
                    <Select.Option value="CREDIT">CREDIT</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Type"
                  name="type"
                  rules={[{ required: true }]}
                >
                  <Select showSearch>
                    <Select.Option value="ACH_DEBIT">ACH_DEBIT</Select.Option>
                    <Select.Option value="ACH_CREDIT">ACH_CREDIT</Select.Option>
                    <Select.Option value="WIRE_OUTGOING">
                      WIRE_OUTGOING
                    </Select.Option>
                    <Select.Option value="WIRE_INCOMING">
                      WIRE_INCOMING
                    </Select.Option>
                    <Select.Option value="CHECK_PAID">CHECK_PAID</Select.Option>
                    <Select.Option value="CHECK_DEPOSIT">
                      CHECK_DEPOSIT
                    </Select.Option>
                    <Select.Option value="ACCT_XFER">ACCT_XFER</Select.Option>
                    <Select.Option value="FEE_TRANSACTION">
                      FEE_TRANSACTION
                    </Select.Option>
                    <Select.Option value="DEPOSIT">DEPOSIT</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Description" name="description">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Reference" name="reference">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Amount"
                  name="amount"
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    step={0.001}
                    precision={decimalPrecision}
                    formatter={(value) =>
                      `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value.replace(/[^\d.-]/g, "")}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>

      {/* Invoice View Modal */}
      <InvoiceViewModal
        visible={viewInvoiceModalVisible}
        onCancel={() => {
          setViewInvoiceModalVisible(false);
          setViewingInvoiceId(null);
        }}
        invoiceId={viewingInvoiceId}
        invoiceType="bill"
        isCredit={false}
      />
    </div>
  );
};

export default TransactionsAllocations;
